import type { Realtime, TokenRequest } from 'ably';
import Ably from 'ably';
import { leaveBreadcrumb, notifyError } from 'src/utilities/monitoring.js';
import { customElement } from 'lit/decorators.js';
import { DAppCore } from 'src/d-app-core.js';
import { getPersistedAuthState } from 'src/auth/persisted-auth.js';
import { isOAuthProcessing } from 'src/auth/auth-storage.js';
import { queueFiles, notifyQueueChanged } from 'src/services/substance-upload-queue.js';
import { setApiClient, processUploads, resumePendingUploads } from 'src/services/substance-upload-processor.js';
import type {
  DeleteTimeCorrectionEvent,
  UpdateTimeCorrectionEvent,
} from 'src/library/lists/d-timekeeping-list-section.js';
import { mapSearchResultHits } from 'src/models/search.js';
import { getCurrentLogoutOptions, handleNavigation, logOut, reduxConnectMixin } from 'src/redux-behavior.js';
import { getStateClient } from 'src/client/state-client.js';
import type { SWToClientMessage } from 'src/worker/message-types.js';
import {
  applyTemporaryPageGroupOverrides,
  cacheTutorialContent,
  createAsset,
  createContract,
  createEmployee,
  createPartner,
  createRiskAssessment,
  createTutorialRegistration,
  declineTaskTemplateUpdate,
  declineTemplateUpdate,
  deleteAttachment,
  deleteItem,
  fetchPageGroups,
  fetchParticipantState,
  fetchTutorialContent,
  fetchTutorials,
  loadedPageGroups,
  loadedTutorials,
  loadOrganization,
  loginUserAction,
  renameAttachment,
  saveItem,
  sendMessage,
  sendShareVacation,
  stopShareVacation,
  taskDone,
  updateCopilotOpen,
  updateChatOpen,
  updateChatMode,
  updateCurrentHelpPage,
  updateCurrentSideContentTemplateId,
  updateCurrentTutorialParticipantId,
  updateCurrentTutorialParticipantState,
  updateHelpViewerOpen,
  updateItem,
  updateOrganization,
  updateParticipantState,
  updateRecognizedText,
  updateSelectedStartTask,
  updateSideContentOpen,
  updateStaffingCalendarYear,
  updateTutorialViewerOpen,
  updateUserAction,
  updateUserProperties,
  updateUserPropertiesForOrganization,
  updateWeekStart,
  uploadFile,
  reloadScreens,
  setRelatedEventsForRiskAssessment,
  setRelatedTasksForRiskAssessment,
  setMeasuresCardPublished,
  onFetchRejected,
  updateItemAction,
} from 'src/store/actions';
import { checklistKeyForEventOccurrence, checklistSaveCoordinator } from 'src/utilities/checklist-save-coordinator';
import {
  breadCrumbSelectedName,
  currentEmployeeAsViewModel,
  currentEmployeeShortName,
  currentEntityTypeNameDetermined,
  currentUserUuid,
  employeeNamesById,
  getItemDataFromTemplateId,
  getOrganization,
  ignoredRobotAlerts,
  isLoadedForCurrentOrg,
  itemsByTypeAndId,
  loaded,
  partnerNamesById,
  writeAccess,
} from 'src/store/selectors';
import { selectLocalSearchResults } from 'src/store/selectors/offline-search';
import { BASE_PATH, getStore } from 'src/store';
import type {
  AccountStateViewModel,
  AccountUpdateMessage,
  AccountUpdateMessageReceiveRemindersEnum,
  AccountUpdateMessageSummariesEnum,
  AddToTopicRequest,
  ConnectEventOccurrencesRequest,
  CreateEventOccurrenceRequest,
  CreateMeetingOccurrenceReportRequest,
  CreateOrUpdateEmployeeRequest,
  CreateOrUpdateFunctionRequest,
  CreateOrUpdatePartnerContactRequest,
  CreateOrUpdatePartnerRequest,
  CreateTopicRequest,
  DeleteEventOccurrenceRequest,
  DeleteMeetingOccurrenceRequest,
  DeleteTopicRequest,
  EmployeeViewModelGenderEnum,
  FunctionUpdateMessage,
  IssueUpdateMessage,
  LeavePeriod,
  MarkEventOccurrenceDoneRequest,
  MarkEventOccurrenceNotDoneRequest,
  MarkMeetingOccurrenceDoneRequest,
  MarkMeetingOccurrenceNotDoneRequest,
  OrganizationReference,
  OrganizationSettingsUpdateMessage,
  OrganizationSettingsUpdateMessageInvoiceSendMethodEnum,
  OrganizationSettingsUpdateMessageSpecialTermsEnum,
  OrganizationUpdateMessage,
  PlusTimePeriod,
  RepeatEventOccurrenceRequest,
  RepeatMeetingOccurrenceRequest,
  RequestSignaturesSignatory,
  SaveDraftRequest,
  SendMeetingOccurrenceNoticeRequest,
  SendMeetingOccurrenceReportRequest,
  TimekeepingCorrection,
  UpdateEventOccurrenceRelatedAssetRequest,
  UpdateEventOccurrenceRequest,
  UpdateFormResponseForReportRequest,
  UpdateMeetingOccurrenceCommandClassificationEnum,
  UpdateMeetingOccurrenceRequest,
  UpdateSpecialTermsCommandSpecialTermsEnum,
  UpdateEmergencyAccessRequest,
  UpdateStaffingAccessRequest,
  UpdateSubstancePdfRequest,
  UpdateTopicRequest,
  WorkSchedule,
  WorkScheduleException,
} from '@trinnvis/dabih-api-client';
import {
  type CreateOrUpdateComputerRequest,
  type CreateOrUpdateNetworkRequest,
  type EmployeeUpdateMessageAccessLevelEnum,
  type NetworkUpdateMessageNetworkTypeEnum,
  SendMessageCommandMessageTypeEnum,
} from '@trinnvis/dabih-api-client';
import { displayAlert } from 'src/utilities/display-alert.js';
import { validateTutorialRegistration } from 'src/utilities/validate-tutorial-registration.js';
import { AblyBatchNotificationHandler } from 'src/utilities/ably-batch-notification-handler.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { uuid } from 'src/utilities/text.js';
import { t } from 'src/i18n';
import { applicationViewModel } from './models/application-view-model.js';
import type { State, User } from './store/types.js';
import { updateMessage } from 'src/d-app--update-message.js';
import { dataItemChanged } from 'src/d-app--data-item-changed.js';
import {
  accessChangeHandler,
  backupChangeHandler,
  computerNetworkChangeHandler,
  dataItemsRemovedHandler,
  externalConnectChangeHandler,
  mapElementChangedHandler,
  mapElementDeletedHandler,
} from 'src/handlers/computers-handlers.js';
import { findActiveParticipantId, findParticipantIdForTutorial } from 'src/models/tutorials.js';
import type { UpdateUserAccessEvent } from 'src/layout/parts/d-organization-edit-access.js';
import { produce } from 'immer';
import _, { isEqual } from 'lodash';
import { handleCreateEntity } from 'src/d-app-create-entity-handler';
import { getNewItemLabels } from 'src/utilities/new-item-labels';
import type { URLOpenListenerEvent } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { runningUnderNativePlatform } from 'src/store/running-under-native-platform';
import { saveTokens, openHelseIdAuth, openCriiptoAuth, getValidAccessToken } from 'src/store/oauth-browser';
import type { CollectionEditDoneDetail } from './content/collection-edit-done-detail';
import type { ContentEditDoneDetail } from './content/content-edit-done-detail';
import type { RelateAssetToEventOccurrencesDetail } from './content/relate-asset-to-event-occurrences-detail';
import type { CollectionFilter } from './store/selectors/collectionFilter';
import type { UploadedFile } from './pages/substances-page/uploaded-file';
import type { EmployeeWithStaffGroup } from './pages/staffing-page/employee-with-staff-group';
import type { EditException } from './pages/staffing-page/edit-exception';
import type { ShareVacationResult } from './pages/staffing-page/share-vacation-result';
import type { ZoomUnit } from './pages/staffing-page/zoom-unit';
import type { OrganizationEditItem } from './pages/organization-page/organization-edit-item';
import type { EditPeriodResult } from './pages/edit-period-result';
import type { NetworkExternalConnectionChange } from './pages/computers-page/infosec-procedure/editors/network-external-connection-change';
import type { ComputerNetworkChange } from './pages/computers-page/infosec-procedure/editors/computer-network-change';
import type { BackupChange } from './pages/computers-page/infosec-procedure/editors/backup-change';
import type { InfosecNetwork } from './pages/computers-page/infosec-procedure/infosecNetwork';
import type { AccessChange } from './pages/computers-page/infosec-procedure/access-change';
import type { UserForSignup } from './outskirts/signup/user-for-signup';
import type { NewOrganization } from './outskirts/signup/new-organization';
import type { TutorialState } from './outskirts/tutorial-state';
import type { CreateEntityInput } from './layout/parts/create-entity-input';
import type { RiskAssessmentRelationsChangedDetail } from './content/risk-assessments/risk-assessment-relations-changed-detail.js';
import type { RiskAssessmentMeasuresCardChangedDetail } from './content/risk-assessments/risk-assessment-measures-card-changed-detail.js';
import type { AppStateSignin } from './app-state-signin';
import type { AppStateSignup } from './app-state-signup';
import type { AppStateSecured } from './app-state-secured';
import type { AppStateNoUser } from './app-state-no-user';
import type { AppStateLink } from './app-state-link';
import type { AppStateNotFound } from './app-state-not-found';
import type { AppStateLoading } from './app-state-loading';
import type { AppStateAccount } from './app-state-account';
import type { AppStateDashboard } from './app-state-dashboard';
import type { AppStateMe } from './app-state-me';
import type { AppStateIssueMessages } from './app-state-issue-messages';
import type { AppState } from './app-state';
import type { MapElementChangedDetail } from './map-element-changed-detail';
import type { EventDoneDetail } from './content/event-occurrences/event-done-detail';
import type { CreateEmployeeInput } from './content/meeting-occurrences/create-employee-input';
import type { MessageForSend } from './content/meeting-occurrences/message-for-send';
import type { CreateContactAndPartnerInput } from './layout/parts/create-contact-and-partner-input';
import type { Subscription } from './layout/parts/subscription';
import type { FileViewerDocument } from './layout/parts/file-viewer-document';
import type { ContentItem } from './layout/parts/content-item';
import type { CreatePartnerEventDetail } from './layout/parts/create-partner-event-detail';
import { checkEmail, createClient, createUser, debouncedSearch, sendPassword } from './store/backend';
import { getPages } from './store/selectors/pages/getPages';
import { isPageId } from './store/selectors/pages/isPageId';
import { getFeatureStates } from './store/selectors/features/getFeatureStates';
import {
  uploadScreenMedia,
  deleteScreen,
  fetchLibrary,
  removeScreenMedia,
  pairScreen,
  orderScreen,
  updateScreen,
} from './store/signage';
import type { UpdateScreenInput } from './store/update-screen-input';
import type { ScreenMediaUploadPayload } from './content/screens/screen-media-upload-payload';
import type { RemoveMediaPayload } from './content/screens/remove-media-payload';
import type { WorkScheduleWeek } from '@trinnvis/dabih-api-client';
import type { Slide } from './content/screens/slide-element';
import type { UpdateFormValuesDetail } from './pages/status-page/update-form-values-detail';
import type { FeedbackResult } from './layout/parts/feedback-result';
import { DFeedbackDialog } from './layout/parts/d-feedback-dialog';
import { NewIssueDialog } from './content/issues/new-issue-dialog';
import { trace } from './utilities/trace';
import { documentTitleForAppState } from 'src/utilities/document-title';
import type { PropertyValues } from 'lit';

@customElement('d-app')
class DApp extends reduxConnectMixin(DAppCore) {
  private employeeNamesById: { [uuid: string]: string } = {};
  private partnerNamesById: { [uuid: string]: string } = {};
  private currentAblyTokenRequest?: string;
  // Multi-org Ably: we subscribe to a channel per eligible org so cache stays
  // warm. The active org (the one the user is looking at) drives Redux —
  // non-active orgs only update SW state.
  private subscribedAblyOrgIds = new Set<number>();
  private batchNotificationHandlers = new Map<number, AblyBatchNotificationHandler>();
  private ablyClient?: Realtime;
  private networkOnlineHandler?: () => void;
  private networkOfflineHandler?: () => void;

  private api = createClient();
  private lastDraftSaved?: SaveDraftRequest;
  private uploadResumeAttemptedForOrganization?: string;

  private debouncedDoStateChanged = _.debounce((state: State) => this.doStateChanged(state), 400, {
    trailing: true,
    leading: true,
  });
  private debouncedTutorialStateChanged = _.debounce(
    (detail: TutorialState) => this.performTutorialStateUpdate(detail),
    300,
    {
      leading: true,
      trailing: true,
    },
  );

  // Debounced functions for tutorial Ably notifications
  private debouncedReloadTutorials = _.debounce(
    () => {
      const state = getStore().getState();
      if (state.id) {
        this.reloadTutorials();
      }
    },
    500,
    { leading: false, trailing: true },
  );

  // Debounced screen reload for signage Ably notifications (dabih-api #4546).
  // Koalescerer en byge av skjerm-/bibliotek-endringer til én reloadScreens
  // (som er N+1: lista + detaljer per skjerm), så vi ikke stormer API-et.
  private debouncedReloadScreens = _.debounce(
    () => {
      const state = getStore().getState();
      if (state.id) {
        void getStore().dispatch(reloadScreens(state.id));
      }
    },
    500,
    { leading: false, trailing: true },
  );

  private debouncedRefetchCurrentParticipantState = _.debounce(
    (tutorialId: number, participantId: string) => {
      const state = getStore().getState();
      if (state.id) {
        fetchParticipantState(this.api, state.id, tutorialId, participantId)
          .then((participantState) => {
            getStore().dispatch(updateCurrentTutorialParticipantState(participantState));
          })
          .catch((error) => {
            console.error('[Tutorial] Failed to refetch participant state:', error);
          });
      }
    },
    500,
    { leading: false, trailing: true },
  );

  private deepLinkListenerHandle?: PluginListenerHandle;
  private ablyReconnectAttempts = 0;
  private ablyReconnectTimer?: number;
  private stateClientUnsubscribe?: () => void;
  private lastSWUserKey?: string; // `${username}|${bearerToken}` — re-pushes on token rotation
  // Sekvensnummer for updateAppStateInner. Hver invokering får et nytt nummer.
  // Etter `await` sjekkes det mot siste-utstedte: hvis ikke samme, ble en nyere
  // invokering startet og denne er stale (gammel state) — vi skal IKKE overskrive
  // this.appState. debouncedDoStateChanged kjører leading+trailing, så flere
  // updateAppStateInner-kall kan overlappe: et eldre kall (user satt, org ikke
  // lastet → 'loading') kan ferdigstille ETTER et nyere kall (org lastet →
  // 'account') og overskrive account → loading (synlig konto→laster-flimmer).
  private appStateSeq = 0;

  stateChanged(state: State) {
    this.debouncedDoStateChanged(state);

    // Tell the SW about the user (with auth context). The SW handles its own
    // org fetching and persistence — d-app no longer mirrors org data.
    void this.pushUserToServiceWorker(state);
  }

  /**
   * Push the current user (with apiBase + bearerToken) to the SW so it can
   * fetch orgs on its own. Deduplicated by `${username}|${bearerToken}` so we
   * re-push automatically on token rotation but not on unrelated Redux ticks.
   */
  private async pushUserToServiceWorker(state: State): Promise<void> {
    if (!('serviceWorker' in navigator) || !state.user) {
      return;
    }
    let bearerToken: string | undefined;
    try {
      bearerToken = await getValidAccessToken();
    } catch (error) {
      console.warn('[DApp] getValidAccessToken failed', error);
      return;
    }
    if (!bearerToken) {
      return;
    }
    const key = `${state.user.username}|${bearerToken}`;
    if (key === this.lastSWUserKey) {
      return;
    }
    this.lastSWUserKey = key;
    void getStateClient().postMessage({
      type: 'SET_USER',
      payload: {
        user: state.user,
        apiBase: BASE_PATH,
        bearerToken,
        appVersion: __APP_VERSION__,
      },
    });
  }

  doStateChanged(state: State) {
    this.updateAppState(state);
  }

  // Returns the active org id only when the loaded organization data matches state.id.
  // Use this at action time instead of trusting a possibly-stale viewModel.loaded — it
  // closes the org-switch race where state.loaded stayed true while state.organization
  // still pointed at the previous org.
  private currentOrgIdIfLoaded(): number | undefined {
    const state = getStore().getState();
    return isLoadedForCurrentOrg(state) && state.id !== null ? state.id : undefined;
  }

  async subscribeToAbly(user: User, _organizationId: number) {
    if (user.ablyTokenRequest && this.currentAblyTokenRequest !== user.ablyTokenRequest) {
      this.currentAblyTokenRequest = user.ablyTokenRequest;
      const lastToken: Ably.TokenRequest | undefined = JSON.parse(user.ablyTokenRequest);
      this.ablyClient = this.createAblyClient(lastToken, user);
      // New token / new client — old channel handles are dead. Reset state so we
      // re-subscribe everything below.
      this.subscribedAblyOrgIds.clear();
      this.batchNotificationHandlers.clear();
    }
    if (!this.ablyClient || !user.ablyTokenRequest) {
      return;
    }

    // Subscribe once per eligible org. Channels stay alive for the lifetime of
    // the Ably client; the active-org gate happens inside the per-channel handler.
    for (const org of user.organizations) {
      if (this.subscribedAblyOrgIds.has(org.id)) {
        continue;
      }
      if (org.pending) {
        continue;
      }
      if (org.requiresSecureLogin && !user.provider) {
        continue;
      }
      this.subscribedAblyOrgIds.add(org.id);
      const suffix = org.userType === 'CONTACT' ? '-contact' : '';
      await this.startListeningToChannels(this.ablyClient, org.id, suffix, user);
    }
  }

  async onWeekStartChanged(e: CustomEvent<{ value: string }>) {
    getStore().dispatch(updateWeekStart(e.detail.value));
  }

  async onStaffingCalendarUserDisplaySelectionChanged(
    e: CustomEvent<{
      start: string;
      end: string;
      zoom: ZoomUnit;
    }>,
  ) {
    const orgId = this.currentOrgIdIfLoaded();
    if (orgId !== undefined && this.appState?.page === 'account') {
      await getStore().dispatch(
        updateUserPropertiesForOrganization(this.api, { staffingCalendarUserDisplaySelection: e.detail }, orgId),
      );
    }
  }

  /**
   * For utvikling
   *
   */
  async onUpdateUiSettings(e: CustomEvent<{ isSimplifiedUi: boolean }>) {
    const orgId = this.currentOrgIdIfLoaded();
    if (orgId !== undefined && this.appState?.page === 'account') {
      await getStore().dispatch(updateUserPropertiesForOrganization(this.api, { uiSettings: e.detail }, orgId));
    }
  }

  async onUpdateEmergencySettings(e: CustomEvent<{ isUserEmergencyMode: boolean }>) {
    const orgId = this.currentOrgIdIfLoaded();
    if (orgId !== undefined && this.appState?.page === 'account') {
      await getStore().dispatch(
        updateUserPropertiesForOrganization(this.api, { isUserEmergencyMode: e.detail.isUserEmergencyMode }, orgId),
      );
    }
  }

  async onUpdateCollectionsFilter(e: CustomEvent<CollectionFilter[]>) {
    const orgId = this.currentOrgIdIfLoaded();
    if (orgId !== undefined && this.appState?.page === 'account') {
      await getStore().dispatch(updateUserPropertiesForOrganization(this.api, { collectionsFilter: e.detail }, orgId));
    }
  }

  async onSaveHideDoneOption(e) {
    const orgId = this.currentOrgIdIfLoaded();
    if (orgId !== undefined && this.appState?.page === 'account') {
      await getStore().dispatch(
        updateUserPropertiesForOrganization(this.api, { todoListHideAfterDaysOption: e.detail.value }, orgId),
      );
    }
  }

  async saveOrganizationLoadTime() {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      const organizationId = this.appState.viewModel.currentOrganizationId;
      const user = this.appState.user;
      const timestamp = Date.now();
      let lastTimestamp = 0;
      for (const key of Object.keys(user)) {
        if (key === organizationId + '_lastLoaded') {
          if (typeof user[key] === 'number') {
            lastTimestamp = user[key];
          }
        }
      }
      if (timestamp > lastTimestamp + 5000) {
        await getStore().dispatch(
          updateUserPropertiesForOrganization(
            this.api,
            { lastLoaded: timestamp },
            this.appState.viewModel.currentOrganizationId,
          ),
        );
      }
    }
  }

  async onUploadFile(e: CustomEvent<{ file: File; callback: () => void; type: string; uuid: string }>) {
    await getStore().dispatch(uploadFile(this.api, e.detail.type, e.detail.uuid, e.detail.file));
    e.detail.callback();
  }

  async onUpdateSubstanceFile(
    e: CustomEvent<{ files: File[]; substanceId: string; callback: (_data: UploadedFile[]) => void }>,
  ) {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      let hasFiles = false;
      const files: Blob[] = [];
      const requestParameters: UpdateSubstancePdfRequest = {
        organizationId: this.appState.viewModel.currentOrganizationId.toString(),
        substanceId: e.detail.substanceId,
      };

      for (const file of e.detail.files) {
        const suffix = file.name.slice(-3).toLowerCase();
        if (suffix === 'pdf' || file.type === '') {
          files.push(file);
          hasFiles = true;
        }
      }
      if (hasFiles) {
        const data = await this.api.substances.updateSubstancePdf({ ...requestParameters, filename: files });
        e.detail.callback(data ?? []);
      }
    }
  }

  async onUploadSubstanceFiles(e: CustomEvent<{ files: File[]; callback: () => void }>) {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      const organizationId = this.appState.viewModel.currentOrganizationId.toString();

      // Filter to only PDF files (convert FileList to array first)
      const pdfFiles = Array.from(e.detail.files).filter((file) => {
        const suffix = file.name.slice(-3).toLowerCase();
        return suffix === 'pdf' || file.type === '';
      });

      if (pdfFiles.length === 0) {
        e.detail.callback();
        return;
      }

      // Queue files in IndexedDB for persistence
      await queueFiles(organizationId, pdfFiles);
      await notifyQueueChanged();

      // Callback immediately - don't wait for uploads
      e.detail.callback();

      // Process uploads in background with notification support
      this.processUploadsWithNotification(organizationId, pdfFiles.length);
    }
  }

  async onSaveTimekeepingPeriod(e: CustomEvent<EditPeriodResult>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const m: PlusTimePeriod = {
        confirmed: e.detail.confirmed,
        end: e.detail.end.split(' ')[0],
        notes: e.detail.notes,
        start: e.detail.start.split(' ')[0],
        uuid: e.detail.periodId ?? uuid(),
        startTime: e.detail.start.split(' ')[1] ?? '',
        endTime: e.detail.end.split(' ')[1] ?? '',
      };

      await this.api.employees.createOrUpdateEmployeePlusTimePeriod({
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        entityId: e.detail.employeeUuid,
        periodId: m.uuid,
        plusTimePeriodUpdateMessage: {
          plusTimePeriod: m,
        },
      });
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize substance upload processor with API client
    setApiClient(this.api);

    // Setup network recovery handlers
    this.networkOnlineHandler = () => {
      this.handleNetworkRecovery();
    };

    this.networkOfflineHandler = () => {};

    window.addEventListener('network-online', this.networkOnlineHandler);
    window.addEventListener('network-offline', this.networkOfflineHandler);

    // Setup StateClient listener (Phase 1: logging only)
    if ('serviceWorker' in navigator) {
      const stateClient = getStateClient();
      this.stateClientUnsubscribe = stateClient.onMessage((message: SWToClientMessage) => {
        console.log('[DApp] SW state message:', message.type);
      });
    }

    // Setup deep link handler (only once)
    this.setupDeepLinkHandler();

    // Setup organization management handlers
    this.addEventListener('api-leave-organization', ((e: CustomEvent<{ organizationId: number }>) =>
      this.handleLeaveOrganization(e)) as unknown as EventListener);
    this.addEventListener('api-delete-organization', ((e: CustomEvent<{ organizationId: number }>) =>
      this.handleDeleteOrganization(e)) as unknown as EventListener);

    // Setup tutorial registration handlers
    this.addEventListener('create-tutorial-registration', ((e: CustomEvent) =>
      this.onCreateTutorialRegistration(e)) as unknown as EventListener);
    this.addEventListener('create-guide-registration', ((e: CustomEvent) =>
      this.onCreateGuideRegistration(e)) as unknown as EventListener);
    this.addEventListener('open-guide-registration', ((e: CustomEvent) =>
      this.onOpenGuideRegistration(e)) as unknown as EventListener);
  }

  // Events fra d-risk-assessment-view fanges via @-bindinger i d-app-core sin
  // render (samme mønster som @create-entity), ikke addEventListener — bubbling
  // til host fanges ikke der content-eventene bindes.
  async onRiskAssessmentMeasuresCardChanged(e: CustomEvent<RiskAssessmentMeasuresCardChangedDetail>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const organizationId = '' + this.appState.viewModel.currentOrganizationId;
      const { riskAssessmentId, published } = e.detail;
      try {
        await setMeasuresCardPublished(this.api, organizationId, riskAssessmentId, published);
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  async onRiskAssessmentRelationsChanged(e: CustomEvent<RiskAssessmentRelationsChangedDetail>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const organizationId = '' + this.appState.viewModel.currentOrganizationId;
      const { riskAssessmentId, entityType, uuids } = e.detail;
      try {
        if (entityType === 'eventOccurrences') {
          await setRelatedEventsForRiskAssessment(this.api, organizationId, riskAssessmentId, uuids);
        } else {
          await setRelatedTasksForRiskAssessment(this.api, organizationId, riskAssessmentId, uuids);
        }
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up event listeners
    if (this.networkOnlineHandler) {
      window.removeEventListener('network-online', this.networkOnlineHandler);
    }
    if (this.networkOfflineHandler) {
      window.removeEventListener('network-offline', this.networkOfflineHandler);
    }

    // Clean up deep link listener
    if (this.deepLinkListenerHandle) {
      this.deepLinkListenerHandle.remove();
      this.deepLinkListenerHandle = undefined;
    }

    // Clean up StateClient subscription
    if (this.stateClientUnsubscribe) {
      this.stateClientUnsubscribe();
      this.stateClientUnsubscribe = undefined;
    }
  }

  async onDeleteTimekeepingPeriod(e: CustomEvent<{ employeeUuid: string; periodId: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await this.api.employees.deleteEmployeePlusTimePeriod({
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        entityId: e.detail.employeeUuid,
        periodId: e.detail.periodId,
      });
    }
  }

  async onSaveLeavePeriod(e: CustomEvent<EditPeriodResult>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const m: LeavePeriod = {
        days: e.detail.type === 'sickLeave' && e.detail.grade < 100 ? e.detail.days : [],
        grade: e.detail.type === 'sickLeave' ? e.detail.grade : 100,
        type: e.detail.type,
        confirmed: e.detail.confirmed,
        end: e.detail.end.split(' ')[0],
        notes: e.detail.notes,
        start: e.detail.start.split(' ')[0],
        uuid: e.detail.periodId ?? uuid(),
        startTime: e.detail.start.split(' ')[1],
        endTime: e.detail.end.split(' ')[1],
      };

      await this.api.employees.createOrUpdateEmployeeLeavePeriod({
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        entityId: e.detail.employeeUuid,
        periodId: m.uuid,
        leavePeriodUpdateMessage: {
          leavePeriod: m,
        },
      });
    }
  }

  async onDeleteLeavePeriod(e: CustomEvent<{ employeeUuid: string; periodId: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await this.api.employees.deleteEmployeeLeavePeriod({
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        entityId: e.detail.employeeUuid,
        periodId: e.detail.periodId,
      });
    }
  }

  async onSaveStaffingAccess(e: CustomEvent<{ accessList: string[]; leavePeriodEditRestriction: boolean }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const p: UpdateStaffingAccessRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        updateStaffingAccessCommand: {
          staffingCalendarAccessList: e.detail.accessList,
          leavePeriodEditRestriction: e.detail.leavePeriodEditRestriction,
        },
      };
      await this.api.organization.updateStaffingAccess(p);
      // Optimistic update
      const org = getOrganization(getStore().getState());
      if (org) {
        getStore().dispatch(
          updateItemAction({
            path: '/organizations/' + this.appState.viewModel.currentOrganizationId,
            item: {
              ...org,
              staffingCalendarAccessList: e.detail.accessList,
              leavePeriodEditRestriction: e.detail.leavePeriodEditRestriction,
            },
          }),
        );
      }
    }
  }

  async onSaveEmergencyAccess(e: CustomEvent<{ accessList: string[] }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const p: UpdateEmergencyAccessRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        updateEmergencyAccessCommand: {
          emergencyAccessList: e.detail.accessList,
        },
      };
      await this.api.organization.updateEmergencyAccess(p);
      // Optimistic update
      const org = getOrganization(getStore().getState());
      if (org) {
        getStore().dispatch(
          updateItemAction({
            path: '/organizations/' + this.appState.viewModel.currentOrganizationId,
            item: { ...org, emergencyAccessList: e.detail.accessList },
          }),
        );
      }
    }
  }

  async onRepeatMeetingOccurrence(
    e: CustomEvent<{
      uuid: string;
      dateTimesToAdd: string[];
      occurrencesToRemove: string[];
      applyToCollections: boolean;
    }>,
  ) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const p: RepeatMeetingOccurrenceRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        repeatMeetingOccurrenceCommand: {
          uuid: e.detail.uuid,
          dateTimesToAdd: e.detail.dateTimesToAdd,
          occurrencesToRemove: e.detail.occurrencesToRemove,
          repeatTopics: e.detail.applyToCollections,
        },
      };

      await this.api.meetingOccurrences.repeatMeetingOccurrence(p);
    }
  }

  async onRepeatEventOccurrence(
    e: CustomEvent<{
      uuid: string;
      dateTimesToAdd: string[];
      occurrencesToRemove: string[];
      applyToCollections: boolean;
    }>,
  ) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const p: RepeatEventOccurrenceRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        repeatEventOccurrenceCommand: {
          uuid: e.detail.uuid,
          dateTimesToAdd: e.detail.dateTimesToAdd,
          occurrencesToRemove: e.detail.occurrencesToRemove,
          repeatTopics: e.detail.applyToCollections,
        },
      };

      await this.api.eventOccurrences.repeatEventOccurrence(p);
    }
  }

  async onUpdateEmployeeWorkSchedules(
    e: CustomEvent<{
      employeeUuid: string;
      workSchedules: WorkSchedule[];
    }>,
  ) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await this.api.employees.updateEmployeeWorkSchedules({
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        entityId: e.detail.employeeUuid,
        workSchedulesUpdateMessage: {
          workSchedules: e.detail.workSchedules,
        },
      });
    }
  }

  onStaffingYearChanged(e: CustomEvent<{ value: string }>) {
    getStore().dispatch(updateStaffingCalendarYear(e.detail.value));
  }

  async onUpdateEmployeeStaffGroups(e: CustomEvent<{ value: EmployeeWithStaffGroup[] }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      for (const employee of e.detail.value) {
        await this.api.employees.updateEmployeeStaffGroup({
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          entityId: employee.uuid,
          updateStaffGroupCommand: {
            staffGroup: employee.staffGroup,
          },
        });
      }
    }
  }

  onUpdateDeclined(e: CustomEvent<{ entityType: string; entityId: string }>) {
    if (e.detail.entityType === 'tasks') {
      getStore().dispatch(declineTaskTemplateUpdate(this.api, e.detail.entityId));
    } else {
      getStore().dispatch(declineTemplateUpdate(this.api, e.detail.entityType, e.detail.entityId));
    }
  }

  async onContentSaveEvent(e: CustomEvent<ContentEditDoneDetail>) {
    if (
      this.appState &&
      this.appState.page === 'account' &&
      this.appState.viewModel?.loaded &&
      e.detail.type === 'eventOccurrences'
    ) {
      if (e.detail.message === '!!') {
        const x: CreateEventOccurrenceRequest = {
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          createEventOccurrenceCommand: {
            uuid: e.detail.uuid,
            eventUuid: e.detail.uuid,
          },
        };
        await this.api.eventOccurrences.createEventOccurrence(x);
      }
      const p: UpdateEventOccurrenceRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        updateEventOccurrenceCommand: {
          uuid: e.detail.uuid,
          saveType: e.detail.saveType === 'single' || e.detail.saveType === 'singleRepeated' ? 'THIS' : 'FUTURE',
          alerts: e.detail.alert,
          alertMessage: e.detail.message,
          alertIncludesDetails: e.detail.includeDetailsInAlert,
          name: e.detail.editItem.type === 'standard' ? e.detail.editItem.name : '',
          date: e.detail.editItem.date,
          time: e.detail.editItem.time === 'NONE' ? '' : e.detail.editItem.time,
          durationMinutes: e.detail.editItem.durationMinutes,
          employees: e.detail.editItem.assignedToEmployees,
          contacts: e.detail.editItem.assignedToContacts,
          functionUuid: e.detail.editItem.assignedToFunction,
          assets: e.detail.editItem.assets,
          persistent: e.detail.editItem.persistent,
          reminder: e.detail.editItem.reminder,
          notes: e.detail.editItem.notes,
          description: e.detail.editItem.procedures,
          taskUuid: e.detail.editItem.taskUuid,
        },
      };

      await this.api.eventOccurrences.updateEventOccurrence(p);
      if (e.detail.afterSave) {
        await e.detail.afterSave();
      }
    }
  }

  async onRelateAssetToEventOccurrences(e: CustomEvent<RelateAssetToEventOccurrencesDetail>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const p: ConnectEventOccurrencesRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        assetId: e.detail.asset,
        assetConnectEventOccurrencesCommand: {
          eventOccurrenceIds: e.detail.eventOccurrences,
        },
      };

      await this.api.assets.connectEventOccurrences(p);
    }
  }

  async onContentSaveMeetingOccurrence(e: CustomEvent<ContentEditDoneDetail>) {
    if (
      this.appState &&
      this.appState.page === 'account' &&
      this.appState.viewModel?.loaded &&
      e.detail.type === 'meetingOccurrences'
    ) {
      const p: UpdateMeetingOccurrenceRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        updateMeetingOccurrenceCommand: {
          uuid: e.detail.uuid,
          saveType: e.detail.saveType === 'single' || e.detail.saveType === 'singleRepeated' ? 'THIS' : 'FUTURE',
          name: e.detail.editItem.name,
          date: e.detail.editItem.date,
          time: e.detail.editItem.time,
          durationMinutes: e.detail.editItem.durationMinutes,
          participants: e.detail.editItem.assignedToEmployees,
          externalParticipants: e.detail.editItem.assignedToContacts,
          reminder: e.detail.editItem.reminder,
          agenda: e.detail.editItem.meetingAgenda,
          classification: e.detail.editItem.classification as UpdateMeetingOccurrenceCommandClassificationEnum,
          accessControl: e.detail.editItem.accessControl,
          responsible: e.detail.editItem.meetingResponsibleFunctionUuid,
          responsibleEmployee: e.detail.editItem.meetingResponsibleEmployeeUuid,
        },
      };

      await this.api.meetingOccurrences.updateMeetingOccurrence(p);
      if (e.detail.afterSave) {
        await e.detail.afterSave();
      }
    }
  }

  async onContentDeleteEvent(
    e: CustomEvent<{ url: string; type: string; uuid: string; saveType: string; alert: boolean }>,
  ) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      window.history.pushState({}, '', e.detail.url);
      await handleNavigation(window.location);
      const p: DeleteEventOccurrenceRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        deleteEventOccurrenceCommand: {
          uuid: e.detail.uuid,
          saveType: e.detail.saveType === 'single' || e.detail.saveType === 'singleRepeated' ? 'THIS' : 'FUTURE',
          alerts: e.detail.alert,
        },
      };

      await this.api.eventOccurrences.deleteEventOccurrence(p);
    }
  }

  async onContentDeleteMeetingOccurrence(
    e: CustomEvent<{ url: string; type: string; uuid: string; saveType: string; alerts: boolean }>,
  ) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      window.history.pushState({}, '', e.detail.url);
      await handleNavigation(window.location);
      const p: DeleteMeetingOccurrenceRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        deleteMeetingOccurrenceCommand: {
          uuid: e.detail.uuid,
          saveType: e.detail.saveType === 'single' || e.detail.saveType === 'singleRepeated' ? 'THIS' : 'FUTURE',
        },
      };

      await this.api.meetingOccurrences.deleteMeetingOccurrence(p);
    }
  }

  /**
   * For de fleste entiteter: slett først (optimistisk update setter deleted=true
   * umiddelbart), så naviger til parent-URL — hindrer «flash» av slettet innhold.
   *
   * Skjermer (`screens`) er motsatt: vi MÅ navigere bort FØR sletting. Ellers
   * trigger reloadScreens en state-endring mens vi fortsatt står på
   * /screens/<id>, og buildScreenView henter den nå-slettede skjermen → 404.
   * (Samme naviger-først-mønster som onEntityDeleteOccurrence over.)
   */
  async onEntityDelete(e: CustomEvent<{ url: string; type: string; uuid: string }>) {
    if (e.detail.type === 'screens') {
      // Naviger bort fra skjerm-detaljsiden før sletting (se doc over).
      window.history.pushState({}, '', e.detail.url);
      await handleNavigation(window.location);
      const o = getStore().getState().organization;
      if (o) {
        await deleteScreen(o.organizationId, Number(e.detail.uuid));
        await getStore().dispatch(reloadScreens(o.organizationId));
      }
      return;
    } else if (e.detail.type === 'collections') {
      await this.deleteCollection(e.detail.uuid);
    } else {
      getStore().dispatch(deleteItem(this.api, e.detail.type, e.detail.uuid));
    }
    // Then navigate to parent URL
    window.history.pushState({}, '', e.detail.url);
    await handleNavigation(window.location);
  }

  async onRequestLogin(
    e: CustomEvent<{ user: { username: string; password: string }; loginFailed: () => void; target?: string }>,
  ) {
    try {
      const r = await this.api.accounts.passwordTokenLogin({
        passwordTokenLoginMessage: {
          username: e.detail.user.username,
          password: e.detail.user.password,
        },
      });

      // Validate API response before accessing properties
      if (!r?.accessToken || !r?.refreshToken) {
        const errorMsg = 'Password login API response missing required fields';
        console.error(errorMsg, {
          hasAccessToken: !!r?.accessToken,
          hasRefreshToken: !!r?.refreshToken,
          responseKeys: r ? Object.keys(r) : 'null/undefined',
        });
        throw new Error(errorMsg);
      }

      await saveTokens(r.accessToken, r.refreshToken);
    } catch (error) {
      console.error(error);
      e.detail.loginFailed();
      return;
    }

    const user = e.detail.user;

    const result = await getStore().dispatch(loginUserAction(this.api, user.username));
    if (result && e.detail.target !== undefined) {
      setTimeout(() => {
        window.location.href = e.detail.target ?? '/';
      }, 200);
      window.history.pushState({}, '', '/account');
      await handleNavigation(window.location);
    } else if (result) {
      window.history.pushState({}, '', '/account');
      await handleNavigation(window.location);
    } else {
      e.detail.loginFailed();
    }
  }

  async onRequestPassword(e: CustomEvent<{ email: string }>) {
    try {
      await sendPassword(e.detail.email);
    } catch {
      displayAlert(t('app.couldNotSendNewPassword'));
    }
  }

  async onSavePrepareState(
    e: CustomEvent<{
      organizationId: string;
      state: AccountStateViewModel;
      saveToServer: boolean;
      afterSave: (() => void) | undefined;
    }>,
  ) {
    if (this.appState !== undefined && this.appState.page === 'account') {
      if (e.detail.saveToServer) {
        await this.api.organization.updatePendingOrganizationState({
          organizationId: e.detail.organizationId,
          accountStateUpdateMessage: e.detail.state,
        });
      }
      const store = getStore();
      const state = store.getState();
      if (state.user) {
        const user = produce(state.user, (draft) => {
          const organization = draft.organizations.find((o) => '' + o.id === e.detail.organizationId);
          if (organization) organization.state = e.detail.state;
        });
        await store.dispatch(updateUserAction(user));
        if (e.detail.afterSave) {
          e.detail.afterSave();
        }
      }
    }
  }

  async _requestLink(e) {
    const user = e.detail;

    const result = await getStore().dispatch(loginUserAction(this.api, user.username));
    if (result) {
      window.history.pushState({}, '', '/account');
      await handleNavigation(window.location);
    }
  }

  async onRequestLogout(e?: CustomEvent<{ redirect?: boolean }>) {
    const redirectToSignin = e?.detail?.redirect === true;
    const logoutOptions = getCurrentLogoutOptions();
    leaveBreadcrumb('Logout requested', {
      provider: logoutOptions.provider || 'unknown',
      redirectToSignin,
    });
    logOut(redirectToSignin, logoutOptions);
  }

  async onDeleteTimeCorrection(e: DeleteTimeCorrectionEvent) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await this.api.employees.deleteEmployeeTimekeepingCorrection({
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        entityId: e.detail.employeeUuid,
        periodId: e.detail.uuid,
      });
    }
  }

  async onUpdateTimeCorrection(e: UpdateTimeCorrectionEvent) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const m: TimekeepingCorrection = {
        correctionType: e.detail.correctionType,
        date: e.detail.date,
        hours: e.detail.hours,
        notes: e.detail.notes,
        uuid: e.detail.uuid,
      };
      await this.api.employees.createOrUpdateEmployeeTimekeepingCorrection({
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        entityId: e.detail.employeeUuid,
        periodId: m.uuid,
        timekeepingCorrectionUpdateMessage: {
          timekeepingCorrection: m,
        },
      });
    }
  }

  async onOpenGuideRegistration(e: CustomEvent<{ participantId: string }>) {
    const { participantId } = e.detail;

    // Set as current participant
    getStore().dispatch(updateCurrentTutorialParticipantId(participantId));

    // Load content for this participant
    await this.loadTutorialContentIfNeeded(participantId);
  }

  async onCreateGuideRegistration(
    e: CustomEvent<{
      tutorialId: number;
      contentVersionId: string;
    }>,
  ) {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      const { tutorialId, contentVersionId } = e.detail;
      const organizationId = this.appState.viewModel.currentOrganizationId;

      try {
        // Create registration with single anonymous participant (NULL employeeUuid for guides)
        const registration = await createTutorialRegistration(this.api, organizationId, tutorialId, {
          registrationId: crypto.randomUUID(),
          participants: [
            {
              id: crypto.randomUUID(),
              employeeUuid: null, // NULL for guides
            },
          ],
          dueDate: null, // NULL for guides
          contentVersionId,
          comment: null,
        });
        // Get the participant ID from the created registration
        if (!registration.participants || registration.participants.length === 0) {
          throw new Error('No participants in created registration');
        }
        const participantId = registration.participants[0].id;

        // Set as current participant
        getStore().dispatch(updateCurrentTutorialParticipantId(participantId));

        // Load content and participant state
        const cacheKey = `${tutorialId}-${contentVersionId}`;
        const state = getStore().getState();

        try {
          const promises: Promise<void>[] = [];

          // Fetch content if not already cached
          if (!state.tutorialContentCache[cacheKey]) {
            promises.push(
              fetchTutorialContent(this.api, organizationId, tutorialId, contentVersionId).then((content) => {
                getStore().dispatch(cacheTutorialContent(cacheKey, content));
              }),
            );
          }

          // Always fetch participant state (for new registrations, this will be initial state)
          promises.push(
            fetchParticipantState(this.api, organizationId, tutorialId, participantId).then((participantState) => {
              getStore().dispatch(updateCurrentTutorialParticipantState(participantState));
            }),
          );

          await Promise.all(promises);
        } catch (error) {
          console.error('[Guide Registration] Failed to load content/state:', error);
          throw error;
        }

        // Reload tutorials to update UI with new registration
        // Only call reloadTutorials() - calling reloadSpecificTutorial() in parallel creates a race condition
        // where LOADED_CACHED_ORGANIZATION can overwrite the tutorials array with stale data
        await this.reloadTutorials();
      } catch (error) {
        console.error('[Guide Registration] Failed to create registration:', error);
        displayAlert(t('app.couldNotStartGuide', { message: (error as Error).message }));
      }
    }
  }

  async onCreateTutorialRegistration(
    e: CustomEvent<{
      tutorialId: number;
      contentVersionId: string;
      participants: Array<{ uuid?: string; employeeUuid?: string | null }>;
      dueDate: string;
      comment: string;
    }>,
  ) {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      const { tutorialId, contentVersionId, participants, dueDate, comment } = e.detail;
      const organizationId = this.appState.viewModel.currentOrganizationId;

      // Build request participants
      const requestParticipants = participants.map((p) => ({
        id: crypto.randomUUID(),
        employeeUuid: p.uuid || p.employeeUuid || null,
      }));

      // Derive isGuide from participants data:
      // Guide = 1 participant without employeeUuid
      // Course = 1+ participants with employeeUuid
      const isGuide = requestParticipants.length === 1 && !requestParticipants[0].employeeUuid;

      try {
        // Generate registration UUID
        const registrationId = crypto.randomUUID();

        // Validate request
        const validation = validateTutorialRegistration({
          isGuide,
          participants: requestParticipants,
          dueDate: dueDate || null,
        });

        if (!validation.valid) {
          throw new Error(validation.error);
        }

        const request = {
          registrationId,
          participants: requestParticipants,
          dueDate: dueDate || null,
          contentVersionId,
          comment: comment || null,
        };

        // Call API
        await createTutorialRegistration(this.api, organizationId, tutorialId, request);

        // Reload tutorial data to reflect new registration
        // Only call reloadTutorials() - calling reloadSpecificTutorial() in parallel creates a race condition
        // where LOADED_CACHED_ORGANIZATION can overwrite the tutorials array with stale data
        await this.reloadTutorials();
      } catch (error) {
        console.error('[Tutorial Registration] Failed to create registration:', error);
        displayAlert(t('app.couldNotRegisterParticipants', { message: (error as Error).message }));
      }
    }
  }

  onUpdateRecognizedText(e: CustomEvent<{ text?: string }>) {
    getStore().dispatch(updateRecognizedText(e.detail.text));
  }

  async onToggleHelp(e: CustomEvent<{ value: boolean }>) {
    getStore().dispatch(updateHelpViewerOpen(e.detail.value));
    getStore().dispatch(updateTutorialViewerOpen(false));
    getStore().dispatch(updateSideContentOpen(false));
  }

  async onToggleChat() {
    const state = getStore().getState();
    const newChatOpen = !state.chatOpen;

    // Close other viewers when opening chat
    if (newChatOpen) {
      getStore().dispatch(updateHelpViewerOpen(false));
      getStore().dispatch(updateTutorialViewerOpen(false));
      getStore().dispatch(updateSideContentOpen(false));
      getStore().dispatch(updateCopilotOpen(false));
    }

    getStore().dispatch(updateChatOpen(newChatOpen));
  }

  async onCloseChat() {
    getStore().dispatch(updateChatOpen(false));
  }

  async onChangeChatMode(e: CustomEvent<{ mode: 'ai' | 'user' }>) {
    getStore().dispatch(updateChatMode(e.detail.mode));
  }

  async onToggleSideContent(e: CustomEvent<{ value: boolean }>) {
    getStore().dispatch(updateSideContentOpen(e.detail.value));
    getStore().dispatch(updateHelpViewerOpen(false));
    getStore().dispatch(updateTutorialViewerOpen(false));
  }

  async onToggleTutorial(e: CustomEvent<{ value: boolean }>) {
    const state = getStore().getState();

    let participantIdToUse: string | undefined = state.currentTutorialParticipantId;

    if (state.currentTutorialParticipantId === undefined) {
      const organization = getOrganization(state);
      if (organization === undefined) {
        throw new Error('Illegal state (E487), expected organization');
      }
      const participantId = findActiveParticipantId(organization, currentEmployeeAsViewModel(state));
      if (participantId) {
        getStore().dispatch(updateCurrentTutorialParticipantId(participantId));
        participantIdToUse = participantId; // Use the local variable, not state
      }
    }

    // Load tutorial content if opening and we have a participant
    if (e.detail.value && participantIdToUse) {
      await this.loadTutorialContentIfNeeded(participantIdToUse);
    }

    getStore().dispatch(updateTutorialViewerOpen(e.detail.value));
    getStore().dispatch(updateHelpViewerOpen(false));
  }

  onSaveOrganization(e: CustomEvent<OrganizationEditItem>) {
    const state = getStore().getState();
    const organization = getOrganization(state);
    if (organization !== undefined) {
      const updateMessage: OrganizationUpdateMessage = {
        address: organization.address ?? '',
        notes: e.detail.notes,
        organizationNumber: e.detail.organizationNumber,
        activityCodes: e.detail.activityCodes.slice(),
        businessEntityType: e.detail.businessEntityType,
        mailAddress: e.detail.mailAddress,
        officeAddress: e.detail.officeAddress,
        sector: e.detail.sector,
        type: organization.type ?? '',
        mailPostcode: organization.mailPostcode ?? '',
        url: e.detail.url,
        officePostcode: organization.officePostcode ?? '',
        phone: e.detail.phone,
        name: e.detail.name,
        herNumber: e.detail.herNumber,
        fax: e.detail.fax,
        email: e.detail.email,
      };
      getStore().dispatch(updateOrganization(this.api, updateMessage));
    }
  }

  async updateCollection(detail: CollectionEditDoneDetail) {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      const p: UpdateTopicRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        updateTopicCommand: {
          uuid: detail.uuid,
          name: detail.editItem.name,
          description: detail.editItem.description,
          items: detail.editItem.items.map((i) => i.uuid),
          pages: detail.editItem.pages.map((p) => Number(p)),
        },
      };
      await this.api.topics.updateTopic(p);
    }
  }

  async onUpdateCollections(
    e: CustomEvent<{ collections: CollectionEditDoneDetail[]; newCollection?: CollectionEditDoneDetail }>,
  ) {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      for (const collection of e.detail.collections) {
        await this.updateCollection(collection);
      }
      if (e.detail.newCollection) {
        const collection = e.detail.newCollection;
        const requestParameters: CreateTopicRequest = {
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          createTopicCommand: {
            uuid: collection.uuid,
            pages: [0],
          },
        };
        await this.api.topics.createTopic(requestParameters);
        await this.updateCollection(collection);
      }
    }
  }

  async onAddToCollection(e: CustomEvent<{ collectionUuid: string; entityUuid: string; entityType: string }>) {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      const p: AddToTopicRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        addToTopicCommand: {
          topicUuid: e.detail.collectionUuid,
          entityUuid: e.detail.entityUuid,
        },
      };
      await this.api.topics.addToTopic(p);
    }
  }

  async deleteCollection(uuid: string) {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      const p: DeleteTopicRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        deleteTopicCommand: {
          uuid: uuid,
        },
      };
      await this.api.topics.deleteTopic(p);
    }
  }

  async onAddScreenMedia(e: CustomEvent<ScreenMediaUploadPayload>) {
    const { organizationId, name, mimeType, file } = e.detail;
    await uploadScreenMedia({ organizationId, file, name, mimeType });
    await this.updateAppState(getStore().getState());

    const deadline = Date.now() + 5 * 60 * 1000;
    while (Date.now() < deadline) {
      const library = await fetchLibrary(organizationId);
      const item = library.find((i) => i.name === name);
      if (item && item.assets.length > 0) {
        await this.updateAppState(getStore().getState());
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
    await this.updateAppState(getStore().getState());
  }

  async onRemoveScreenMedia(e: CustomEvent<RemoveMediaPayload>) {
    await removeScreenMedia(e.detail);
    await this.updateAppState(getStore().getState());
  }

  async onPairScreen(e: CustomEvent<{ organizationId: number; screenId: number; playerCode: string }>) {
    await pairScreen(e.detail.organizationId, e.detail.screenId, e.detail.playerCode);
    await getStore().dispatch(reloadScreens(e.detail.organizationId));
  }

  async onPlaceScreenOrder(
    e: CustomEvent<{
      organizationId: number;
      screenId: number;
      currentName: string;
      workWeek: WorkScheduleWeek;
      playlist: Slide[];
    }>,
  ) {
    const { organizationId, screenId, workWeek, playlist } = e.detail;
    try {
      // Lagre eventuelle ulagrede endringer i presentasjonen før bestilling.
      // Navnet mutes ikke lenger – status (TRIAL → ORDERED) settes av backend
      // via /order-endepunktet (#13126, dabih-api #4590).
      await updateScreen({ organizationId, screenId, name: e.detail.currentName, workWeek, playlist });
      await orderScreen(organizationId, screenId);
      await getStore().dispatch(reloadScreens(organizationId));
    } catch (error) {
      console.error('Failed to place screen order:', error);
      displayAlert(t('app.couldNotOrderScreen'));
    }
  }

  async onContentEditDone(e: CustomEvent<ContentEditDoneDetail>) {
    const o = getStore().getState().organization;
    if (o !== undefined) {
      if (e.detail.type === 'collections') {
        await this.updateCollection(e.detail);
        if (e.detail.afterSave) {
          await e.detail.afterSave();
        }
      } else if (e.detail.type === 'screens') {
        const input: UpdateScreenInput = {
          name: e.detail.editItem.name,
          organizationId: o.organizationId,
          workWeek: e.detail.editItem.workWeek,
          playlist: e.detail.editItem.playlist,
          screenId: Number(e.detail.uuid),
        };
        await updateScreen(input);
        await getStore().dispatch(reloadScreens(o.organizationId));
        await this.updateAppState(getStore().getState());
        if (e.detail.afterSave) {
          await e.detail.afterSave();
        }
      } else {
        const entityUuid = e.detail.uuid === '' ? uuid() : e.detail.uuid;
        const message = updateMessage(getStore().getState(), e.detail);
        let parentId: string | undefined = undefined;
        if (e.detail.type === 'contacts') {
          parentId = e.detail.editItem.partnerId;
        } else if (e.detail.type === 'tasks') {
          parentId = e.detail.editItem.functionUuid;
        }

        const entityUuidWithOutInstanceDate = entityUuid.split(':')[0];
        await getStore().dispatch(saveItem(this.api, e.detail.type, entityUuidWithOutInstanceDate, message, parentId));

        // Start timer to verify draft status is removed
        this.verifyDraftRemoval(e.detail.type, entityUuidWithOutInstanceDate, String(o.organizationId));

        if (e.detail.afterSave) {
          await e.detail.afterSave();
        }
      }
    }
  }

  async onSearchQueryChanged(e: CustomEvent<{ query: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      this.searchQuery = e.detail.query;
      const state = getStore().getState();
      const o = state.organization;

      if (o !== undefined) {
        // Check if offline or online
        const isOffline = !navigator.onLine;

        if (isOffline) {
          this.searchResults = selectLocalSearchResults(state, this.searchQuery);
          this.offlineSearch = true;
        } else {
          // Try online search first, fall back to offline on error
          try {
            const hits = await debouncedSearch(
              this.api,
              this.searchQuery,
              String(this.appState.viewModel.currentOrganizationId),
            );
            this.searchResults = mapSearchResultHits(
              this.appState.viewModel.currentOrganizationId,
              hits,
              o.singleUser,
              o,
              this.employeeNamesById,
              this.partnerNamesById,
            );
            this.offlineSearch = false;
          } catch (_error) {
            const currentState = getStore().getState();
            this.searchResults = selectLocalSearchResults(currentState, this.searchQuery);
            this.offlineSearch = true;
          }
        }
      }
    }
  }

  onUpdateHelp(e: CustomEvent<{ page: string }>) {
    getStore().dispatch(updateCurrentHelpPage(e.detail.page));
  }

  async onUpdateTutorial(e: CustomEvent<{ value: string }>) {
    const tutorialId = e.detail.value;
    const state = getStore().getState();
    const organization = getOrganization(state);
    const currentEmployee = currentEmployeeAsViewModel(state);

    if (!organization?.tutorials) {
      console.error('[onUpdateTutorial] No tutorials found in organization');
      return;
    }

    // Find the tutorial
    const tutorial = organization.tutorials.find((t) => t.id === tutorialId);
    if (!tutorial) {
      console.error('[onUpdateTutorial] Tutorial not found:', tutorialId);
      return;
    }

    // Check if this is a guide
    if (!tutorial.guide) {
      // This is a course - find participant ID for this user and tutorial
      const participantId = findParticipantIdForTutorial(tutorialId, organization, currentEmployee);

      if (!participantId) {
        console.error('[onUpdateTutorial] No active participant found for course:', tutorialId);
        return;
      }
      // Open tutorial viewer BEFORE loading content (so user sees loading state)
      getStore().dispatch(updateTutorialViewerOpen(true));

      // Set as current participant
      getStore().dispatch(updateCurrentTutorialParticipantId(participantId));

      // Load content for this participant
      await this.loadTutorialContentIfNeeded(participantId);
      return;
    }

    // This is a guide - always create new registration
    // Open tutorial viewer immediately so user sees loading spinner
    getStore().dispatch(updateTutorialViewerOpen(true));

    // Dispatch event to create registration
    this.dispatchEvent(
      new CustomEvent('create-guide-registration', {
        bubbles: true,
        composed: true,
        detail: {
          tutorialId: parseInt(tutorialId, 10),
          contentVersionId: tutorial.contentVersionId,
        },
      }),
    );
  }

  onUpdateSideContent(e: CustomEvent<{ value: number }>) {
    getStore().dispatch(updateCurrentSideContentTemplateId(e.detail.value));
  }

  async onUpdateEventAsset(
    e: CustomEvent<{
      checkedBy: string;
      checkedTime: string;
      status: string;
      comments: string;
      assetUuid: string;
      eventOccurrenceUuid: string;
    }>,
  ) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      if (!(e.detail.status === 'NONE' || e.detail.status === 'OK' || e.detail.status === 'NOT_OK')) {
        throw new Error('Illegal state (E343) ' + e.detail.status);
      }

      const p: UpdateEventOccurrenceRelatedAssetRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        eventOccurrenceRelatedAssetUpdateMessage: {
          uuid: e.detail.eventOccurrenceUuid,
          assetStatus: e.detail.status,
          comment: e.detail.comments,
          assetUuid: e.detail.assetUuid,
        },
      };

      await this.api.eventOccurrences.updateEventOccurrenceRelatedAsset(p);
    }
  }

  onOpenPage(e: CustomEvent<{ pageId: number; queryString: string }>) {
    const state = getStore().getState();
    const items = itemsByTypeAndId(state);
    const pages = getPages(state);
    const organization = getOrganization(state);

    let target = '';
    // module meetings has a custom page for both employees and partners
    if (
      (e.detail.pageId === 996526 || e.detail.pageId === 312616) &&
      !getFeatureStates(state).core &&
      getFeatureStates(state).meetings
    ) {
      target = '476666';
    } else if (e.detail.pageId === 14785) {
      target = '65/tutorials/1';
    } else if (e.detail.pageId === 14571) {
      target = '65/tutorials/2';
    } else if (e.detail.pageId === 0) {
      target = '695944?showUserSettings';
    } else if (isPageId(e.detail.pageId, pages)) {
      target = '' + e.detail.pageId;
    }
    if (target === '') {
      target = '695944';
    }
    if (items && organization) {
      const itemData = getItemDataFromTemplateId('' + e.detail.pageId, items, organization.singleUser);
      let startTask = '';
      if (itemData) {
        if (itemData.startTask) {
          startTask = itemData.target;
          target = '695944';
        } else {
          target = itemData.target;
        }
      }
      const url =
        '/account/' +
        organization.organizationId +
        '/' +
        target +
        (e.detail.queryString ? '?' + e.detail.queryString : '');
      setTimeout(() => {
        this.dispatchEvent(
          new CustomEvent<{ href: string }>('navigate', { bubbles: true, composed: true, detail: { href: url } }),
        );
      }, 200);
      if (startTask) {
        this.dispatchAction(updateSelectedStartTask(startTask));
      }
    }
    if (this.windowWidth < 801) {
      this.dispatchAction(updateHelpViewerOpen(false));
    }
  }

  onShare(e: CustomEvent<{ message: string; recipients: string[]; subject?: string; url?: string }>) {
    const state = getStore().getState();
    const entityTypeNameDetermined = currentEntityTypeNameDetermined(state) ?? t('app.shareEntityFallback');
    const entityName = breadCrumbSelectedName(state);
    const currentEmployeeName = currentEmployeeShortName(state);
    let subject: string;
    if (e.detail.subject) {
      subject = e.detail.subject;
    } else {
      subject = t('app.shareSubject', {
        name: currentEmployeeName,
        entityType: entityTypeNameDetermined,
        entityName,
      });
    }
    let url: string;
    if (e.detail.url) {
      url = e.detail.url;
    } else {
      const p = state.path;
      const s = p.split('/');
      url = s.length > 3 ? '/' + s.slice(3).join('/') : '';
    }
    const c = {
      subject: subject,
      message: e.detail.message,
      url,
      employees: e.detail.recipients,
      messageType: SendMessageCommandMessageTypeEnum.Email,
    };
    getStore().dispatch(sendMessage(this.api, c));
  }

  async onToggleEventDone(e: CustomEvent<EventDoneDetail>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      if (e.detail.done) {
        const p: MarkEventOccurrenceDoneRequest = {
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          markEventOccurrenceDoneMessage: {
            uuid: e.detail.uuid,
            doneBy: e.detail.doneBy,
            doneDate: e.detail.doneDate,
          },
        };
        await this.api.eventOccurrences.markEventOccurrenceDone(p);
      } else {
        const p: MarkEventOccurrenceNotDoneRequest = {
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          markEventOccurrenceNotDoneMessage: {
            uuid: e.detail.uuid,
          },
        };
        await this.api.eventOccurrences.markEventOccurrenceNotDone(p);
      }
    }
  }

  async onToggleMeetingDone(e: CustomEvent<EventDoneDetail>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      if (e.detail.done) {
        const p: MarkMeetingOccurrenceDoneRequest = {
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          markMeetingOccurrenceDoneMessage: {
            uuid: e.detail.uuid,
            doneBy: e.detail.doneBy,
            doneDate: e.detail.doneDate,
          },
        };
        await this.api.meetingOccurrences.markMeetingOccurrenceDone(p);
      } else {
        const p: MarkMeetingOccurrenceNotDoneRequest = {
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          markMeetingOccurrenceNotDoneMessage: {
            uuid: e.detail.uuid,
          },
        };
        await this.api.meetingOccurrences.markMeetingOccurrenceNotDone(p);
      }
    }
  }

  onShareVacation(e: CustomEvent<ShareVacationResult>) {
    getStore().dispatch(sendShareVacation(this.api, e.detail));
  }

  async onShowShareVacation(e: CustomEvent<{ email: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const result = await this.api.organization.fetchSharedVacationsLink({
        organizationId: this.appState.viewModel.currentOrganizationId,
        email: e.detail.email,
      });
      window.open(result.location, 'super');
    }
  }

  onHideMeet() {
    this.meet = false;
  }

  async onShowMeet(e: CustomEvent<{ meetingOccurrenceId: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();

      const p = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        requestRoomCodeCommand: {
          uuid: e.detail.meetingOccurrenceId,
          personUuid: currentUserUuid(state),
        },
      };
      const roomCodeResponse = await this.api.meetingOccurrences.requestRoomCode(p);
      this.roomCode = roomCodeResponse.code;

      // Store the meeting occurrence ID for private notes
      this.meetingOccurrenceId = e.detail.meetingOccurrenceId;

      // Get the meeting occurrence to fetch the agenda
      const meetingOccurrences = state.organization?.meetingOccurrences || [];
      const meetingOccurrence = meetingOccurrences.find((m) => m.uuid === e.detail.meetingOccurrenceId);
      if (meetingOccurrence) {
        this.meetingAgenda = meetingOccurrence.agenda || '';
      }

      this.meet = true;
    }
  }

  onUnShareVacation(e: CustomEvent<{ recipient: string }>) {
    getStore().dispatch(stopShareVacation(this.api, e.detail));
  }

  onStartTaskSelected(e: CustomEvent<{ uuid: string }>) {
    getStore().dispatch(updateSelectedStartTask(e.detail.uuid));
  }

  onStartTaskClosed() {
    getStore().dispatch(updateSelectedStartTask(''));
  }

  onIgnoreRobot() {
    const state = getStore().getState();
    const e = LocalDate.fromString(state.today).plusDays(1);
    getStore().dispatch(
      updateUserProperties(this.api, {
        ignoreRobotUntil: e.toString(),
      }),
    );
  }

  onIgnoreThisRobotAlert(e: CustomEvent<{ id: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const alerts = ignoredRobotAlerts(state);
      const updatedIgnoredRobotAlerts = [...alerts, e.detail.id];
      getStore().dispatch(
        updateUserPropertiesForOrganization(
          this.api,
          { ignoredRobotAlerts: updatedIgnoredRobotAlerts },
          this.appState.viewModel.currentOrganizationId,
        ),
      );
    }
  }

  async onAssignFunction(e: CustomEvent<{ functionUuid: string; employeeUuid: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        const item = o.functionsById[e.detail.functionUuid];
        if (item !== undefined) {
          const employees =
            item.type === 'SINGLE' ? [e.detail.employeeUuid] : [...item.employees, e.detail.employeeUuid];
          const updateMessage: FunctionUpdateMessage = {
            name: item.name,
            template: item.template,
            category: item.category,
            rotations: item.rotations.map((r) => ({
              employee: r.employee,
              day1: '' + r.day1,
              day2: '' + r.day2,
              day3: '' + r.day3,
              day4: '' + r.day4,
              day5: '' + r.day5,
              day6: '' + r.day6,
              day7: '' + r.day7,
              startDate: r.startDate,
            })),
            status: item.status,
            rotation: item.rotation,
            description: item.description,
            type: item.type,
            employees: employees,
            responsibility: item.responsibility,
            pages: [...item.pages],
            isConfirmedEntity: true,
          };
          const requestParameters: CreateOrUpdateFunctionRequest = {
            organizationId: '' + o.organizationId,
            functionId: e.detail.functionUuid,
            functionUpdateMessage: updateMessage,
          };

          await this.api.functions.createOrUpdateFunction(requestParameters);
        }
      }
    }
  }

  async onUpdateUser(e: CustomEvent<{ email: string; password?: string; alerts: string; receiveReminders: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        const emailOrPasswordChanged = e.detail.email !== this.appState.user.username || e.detail.password;
        const message: AccountUpdateMessage = {
          email: e.detail.email,
          password: e.detail.password,
          organizationIdForSummaries: o.organizationId,
          summaries: e.detail.alerts as AccountUpdateMessageSummariesEnum,
          receiveReminders: e.detail.receiveReminders as AccountUpdateMessageReceiveRemindersEnum,
        };
        await this.api.accounts.updatePassword({
          accountEmail: this.appState.user.username,
          accountUpdateMessage: message,
        });
        if (emailOrPasswordChanged) {
          displayAlert(t('app.emailOrPasswordChanged'));
          await this.onRequestLogout();
        }
      }
    }
  }

  async onAddFeatures(e: CustomEvent<{ features: string[]; healthcareSignatory: string | undefined }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        try {
          for (const feature of e.detail.features) {
            const f = feature;
            await this.api.organization.addFeature({
              organizationId: '' + o.organizationId,
              addFeatureCommand: {
                feature: f,
                signatory: f === 'HEALTHCARE' ? e.detail.healthcareSignatory : undefined,
              },
            });
          }
        } catch (error) {
          console.error('Failed to add features:', error);
          displayAlert(t('app.errorAddingModule'));
        }
      }
    }
  }

  async onToggleFeature(e: CustomEvent<{ feature: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        try {
          const f = o.features ?? [];
          if (f.includes(e.detail.feature)) {
            await this.api.organization.removeFeature({
              organizationId: '' + o.organizationId,
              removeFeatureCommand: {
                feature: e.detail.feature,
              },
            });
          } else {
            await this.api.organization.addFeature({
              organizationId: '' + o.organizationId,
              addFeatureCommand: {
                feature: e.detail.feature,
              },
            });
          }
        } catch (error) {
          console.error('Failed to toggle feature:', error);
          displayAlert(t('app.errorChangingModule'));
        }
      }
    }
  }

  async onUpdateFeatures(e: CustomEvent<{ features: string[] }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        try {
          const oldFeatures = o.features ?? [];
          const newFeatures = e.detail.features;
          for (const newFeature of newFeatures) {
            if (!oldFeatures.includes(newFeature)) {
              await this.api.organization.addFeature({
                organizationId: '' + o.organizationId,
                addFeatureCommand: {
                  feature: newFeature,
                },
              });
            }
          }
          for (const oldFeature of oldFeatures) {
            if (!newFeatures.includes(oldFeature)) {
              await this.api.organization.removeFeature({
                organizationId: '' + o.organizationId,
                removeFeatureCommand: {
                  feature: oldFeature,
                },
              });
            }
          }
        } catch (error) {
          console.error('Failed to update features:', error);
          displayAlert(t('app.errorUpdatingModules'));
        }
      }
    }
  }

  async onUpdateSpecialTerms(e: CustomEvent<{ specialTerms: UpdateSpecialTermsCommandSpecialTermsEnum }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        await this.api.organization.updateSpecialTerms({
          organizationId: '' + o.organizationId,
          updateSpecialTermsCommand: {
            specialTerms: e.detail.specialTerms,
          },
        });
      }
    }
  }

  async onUpdateSubscription(e: CustomEvent<Subscription>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        const message: OrganizationSettingsUpdateMessage = {
          invoiceAddress: e.detail.invoiceAddress,
          reference: e.detail.invoiceReference,
          invoiceReceiver: e.detail.invoiceReceiver,
          invoiceReference: e.detail.invoiceReference,
          ownerEmail: e.detail.ownerEmail,
          invoicePostcode: e.detail.invoicePostcode,
          invoiceLocality: e.detail.invoiceLocality,
          specialTerms: e.detail.specialTerms as OrganizationSettingsUpdateMessageSpecialTermsEnum,
          invoiceSendMethod: e.detail.invoiceSendMethod as OrganizationSettingsUpdateMessageInvoiceSendMethodEnum,
          singleUser: e.detail.singleUser,
          invoiceOrganizationNumber: e.detail.invoiceOrganizationNumber,
        };
        try {
          await this.api.organization.updateOrganizationSettings({
            organizationId: o.organizationId.toString(),
            organizationSettingsUpdateMessage: message,
          });
        } catch (error: any) {
          if (error.response?.status === 422) {
            displayAlert(t('app.cannotReceiveEhfInvoices'));
          } else {
            throw error;
          }
        }
      }
    }
  }

  onCreateIssue(e: CustomEvent<{ id: string; message: IssueUpdateMessage }>) {
    getStore().dispatch(saveItem(this.api, 'issues', e.detail.id, e.detail.message));
  }

  async onUpdateIssueEscalation(
    e: CustomEvent<{
      settings: { enabled: boolean; days: number; recipients: string[]; notifyAssignee: boolean };
      extensions: { uuid: string; deadline: string; reason: string }[];
    }>,
  ) {
    const state = getStore().getState();
    const o = getOrganization(state);
    if (o === undefined) {
      return;
    }
    const organizationId = o.organizationId.toString();
    // Eget endepunkt for avviksinnstillinger (dabih-api#4697), adskilt fra
    // faktura-/abonnementsinnstillingene så de ikke overskriver hverandre.
    await this.api.organization.updateIssueEscalationSettings({
      organizationId,
      issueEscalationSettingsUpdateMessage: {
        issueEscalationEnabled: e.detail.settings.enabled,
        issueEscalationDays: e.detail.settings.days,
        issueEscalationRecipients: e.detail.settings.recipients,
        notifyAssignee: e.detail.settings.notifyAssignee,
      },
    });
    for (const extension of e.detail.extensions) {
      await this.api.issues.extendIssueDeadline({
        organizationId,
        issueId: extension.uuid,
        issueDeadlineExtensionCommand: { deadline: extension.deadline, reason: extension.reason },
      });
    }
    // Last organisasjonen på nytt slik at lagrede innstillinger og nye frister vises.
    if (state.user) {
      getStore().dispatch(loadOrganization(o.organizationId, state.user, this.api));
    }
  }

  async onExtendIssueDeadline(e: CustomEvent<{ uuid: string; deadline: string; reason: string }>) {
    const state = getStore().getState();
    const o = getOrganization(state);
    if (o === undefined) {
      return;
    }
    const organizationId = o.organizationId.toString();
    await this.api.issues.extendIssueDeadline({
      organizationId,
      issueId: e.detail.uuid,
      issueDeadlineExtensionCommand: { deadline: e.detail.deadline, reason: e.detail.reason },
    });
    // Last organisasjonen på nytt slik at ny frist og historikk vises på avviket.
    if (state.user) {
      getStore().dispatch(loadOrganization(o.organizationId, state.user, this.api));
    }
  }

  async onRestore(e: CustomEvent<{ entityType: string; entityId: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        await this.api.organization.restoreDeletedEntity({
          organizationId: o.organizationId.toString(),
          restoreCommand: {
            entityType: e.detail.entityType,
            entityId: e.detail.entityId,
          },
        });
      }
    }
  }

  onChecklistChanged(e: CustomEvent<{ content: string; uuid: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        // Validate that eventOccurrenceId is available before making API call
        if (!e.detail.uuid) {
          console.warn(
            'Cannot update checklist: eventOccurrenceId not available. Checklist changes are only supported for event occurrences.',
          );
          return;
        }
        const occurrence = o.eventOccurrences.find((x) => x.uuid === e.detail.uuid);
        if (occurrence === undefined) {
          // Skjer for meetingOccurrences: d-content sender checklist-changed også for
          // møter, men API-et har ikke noe endepunkt for møtesjekklister.
          console.warn('Cannot update checklist: no event occurrence with uuid ' + e.detail.uuid);
          return;
        }
        const path = '/organizations/' + o.organizationId + '/eventOccurrences/' + e.detail.uuid;
        // Optimistisk dispatch: storen skal aldri ligge bak UI-et (progressbarer m.m.).
        // Serialisering, koalescering og vern mot stale server-snapshots håndteres av
        // checklistSaveCoordinator + checklistPendingMiddleware (issue #13248).
        getStore().dispatch(
          updateItemAction({
            path,
            item: { ...occurrence, description: e.detail.content },
          }),
        );
        checklistSaveCoordinator.save(
          checklistKeyForEventOccurrence(e.detail.uuid),
          e.detail.content,
          async (content) => {
            await this.api.eventOccurrences.updateEventOccurrenceChecklist({
              organizationId: '' + o.organizationId,
              eventOccurrenceId: e.detail.uuid,
              updateChecklistMessage: {
                instance: '',
                content,
              },
            });
          },
          (_key, error) => {
            onFetchRejected(t('actions.saveError'))(error as Error);
            // Hent server-sannheten så store/progressbarer/komponent reverterer synlig.
            getStore().dispatch(updateItem(this.api, { path, eventType: 'update' }));
          },
        );
      }
    }
  }

  async onRestoreRevision(
    e: CustomEvent<{ name: string; content: string; status: string; type: string; uuid: string }>,
  ) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        await this.api.organization.restoreRevision({
          organizationId: '' + o.organizationId,
          restoreRevisionCommand: {
            entityId: e.detail.uuid,
            entityType: e.detail.type,
            name: e.detail.name,
            content: e.detail.content,
            status: e.detail.status,
          },
        });
      }
    }
  }

  async onAddTask(e: CustomEvent<{ taskUuid: string; assetUuid: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        const task = o.tasksById[e.detail.taskUuid];
        if (task !== undefined) {
          await this.api.functions.postFunctionTaskConnectAsset({
            organizationId: '' + o.organizationId,
            functionId: task.functionUuid,
            taskId: task.uuid,
            connectAssetCommand: {
              assetUuid: e.detail.assetUuid,
            },
          });
        }
      }
    }
  }

  async onRemoveTask(e: CustomEvent<{ taskUuid: string; assetUuid: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        const task = o.tasksById[e.detail.taskUuid];
        if (task !== undefined) {
          await this.api.functions.postFunctionTaskDisconnectAsset({
            organizationId: '' + o.organizationId,
            functionId: task.functionUuid,
            taskId: task.uuid,
            disconnectAssetCommand: {
              assetUuid: e.detail.assetUuid,
            },
          });
        }
      }
    }
  }

  async onSaveDraft(
    e: CustomEvent<{ entityType: string; entityUuid: string; draft: Record<string, unknown>; done: () => void }>,
  ) {
    await this.doSaveDraft(e);
    e.detail.done();
  }

  async onClearDraft(e: CustomEvent<{ entityType: string; entityUuid: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        const requestParameters = {
          organizationId: '' + o.organizationId,
          clearDraftCommand: {
            entityType: e.detail.entityType,
            entityUuid: e.detail.entityUuid,
          },
        };
        await this.api.organization.clearDraft(requestParameters);
      }
    }
  }

  async onCreateOtherEntity(organizationId: number, detail: CreateEntityInput) {
    const p = getNewItemLabels().find((x) => x.type === detail.entityType);

    await this.showMessageWithTimeout(
      async () => {
        try {
          await handleCreateEntity(organizationId, detail, this.api, getStore().dispatch);
          // Link the freshly created entity to its risk assessment (set-replace).
          // Done after creation so the uuid is known to the backend. dabih-api#4573.
          if (
            (detail.entityType === 'eventOccurrences' || detail.entityType === 'tasks') &&
            detail.relatedRiskAssessment
          ) {
            const orgId = '' + organizationId;
            const { riskAssessmentId, uuids } = detail.relatedRiskAssessment;
            if (detail.entityType === 'eventOccurrences') {
              await setRelatedEventsForRiskAssessment(this.api, orgId, riskAssessmentId, uuids);
            } else {
              await setRelatedTasksForRiskAssessment(this.api, orgId, riskAssessmentId, uuids);
            }
          }
          this.edit = true;
          window.history.pushState({}, '', detail.targetUrl);
          await handleNavigation(window.location);
        } catch (ex) {
          console.error(ex);
        }
      },
      () => {
        if (p) {
          this.slideNotification({ primaryText: t('app.creatingEntity', { name: p.name.toLowerCase() }) });
        }
      },
      () => {
        if (p) {
          this.showSlideNotification = false;
        }
      },
    );
  }

  async onCreateEntity(e: CustomEvent<CreateEntityInput>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        if (e.detail.entityType === 'collections') {
          const requestParameters: CreateTopicRequest = {
            organizationId: '' + o.organizationId,
            createTopicCommand: {
              uuid: e.detail.entityUuid,
              pages: [e.detail.pageId],
            },
          };
          await this.api.topics.createTopic(requestParameters);
          window.history.pushState({}, '', e.detail.targetUrl);
          await handleNavigation(window.location);
        } else {
          await this.onCreateOtherEntity(o.organizationId, e.detail);
        }
      }
    }
  }

  async onCreateEmployee(e: CustomEvent<CreateEmployeeInput>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        const requestParameters: CreateOrUpdateEmployeeRequest = {
          organizationId: '' + o.organizationId,
          employeeId: e.detail.data.uuid,
          employeeUpdateMessage: {
            isConfirmedEntity: true,
            address: '',
            profession: '',
            notes: '',
            email: e.detail.data.email,
            lastName: e.detail.data.lastName,
            firstName: e.detail.data.firstName,
            herNumber: '',
            hprNumber: '',
            expertise: '',
            accessLevel: e.detail.data.accessLevel as EmployeeUpdateMessageAccessLevelEnum,
            phone: e.detail.data.phone,
            nextOfKin: '',
            secondaryPhone: '',
            status: 'ACTIVE',
            gender: 'UNDEFINED',
          },
        };
        await this.api.employees.createOrUpdateEmployee(requestParameters);
        e.detail.complete();
      }
    }
  }

  async createPartner(uuid: string, name: string) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        const requestParameters: CreateOrUpdatePartnerRequest = {
          organizationId: '' + o.organizationId,
          partnerId: uuid,
          partnerUpdateMessage: {
            isConfirmedEntity: true,
            address: '',
            notes: '',
            name: name,
          },
        };
        await this.api.partners.createOrUpdatePartner(requestParameters);
      }
    }
  }

  async onCreatePartner(e: CustomEvent<CreatePartnerEventDetail>) {
    await this.createPartner(e.detail.data.uuid, e.detail.data.name);
    e.detail.complete();
  }

  async onCreateContactAndPartner(e: CustomEvent<CreateContactAndPartnerInput>) {
    if (e.detail.data.newPartnerName) {
      await this.createPartner(e.detail.data.partnerUuid, e.detail.data.newPartnerName);
    }
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        const requestParameters: CreateOrUpdatePartnerContactRequest = {
          organizationId: '' + o.organizationId,
          partnerId: e.detail.data.partnerUuid,
          contactId: e.detail.data.contactUuid,
          contactPersonUpdateMessage: {
            notes: '',
            email: e.detail.data.email,
            lastName: e.detail.data.lastName,
            firstName: e.detail.data.firstName,
            personalIdentifier: '',
            telephone: '',
            mobilePhone: e.detail.data.phone,
            accessLevel: 'NONE',
            accessExpires: '',
            isConfirmedEntity: true,
          },
        };
        await this.api.partners.createOrUpdatePartnerContact(requestParameters);
        e.detail.complete();
      }
    }
  }

  async onDataItemChanged(e: CustomEvent) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        await dataItemChanged(
          this.api,
          o,
          e,
          state,
          (s: string) => this.slideNotification({ primaryText: s }),
          o.organizationId.toString(),
        );
      }
    }
  }

  async onUpdateFormValues(e: CustomEvent<UpdateFormValuesDetail>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const p2: UpdateFormResponseForReportRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        entityId: e.detail.uuid,
        filteredFormValue: e.detail.content,
      };
      await this.api.reports.updateFormResponseForReport(p2);
    }
  }

  async onComputerNetworkChange(e: CustomEvent<ComputerNetworkChange>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await computerNetworkChangeHandler(this.api, e, this.appState.viewModel.currentOrganizationId.toString());
    }
  }

  async onDataItemsRemoved(e: CustomEvent<{ uuid: string; dataType: string; category: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await dataItemsRemovedHandler(this.api, e);
    }
  }

  async onLinkAuth(e: CustomEvent<{ provider: 'oidc' | 'criipto' }>) {
    if (this.appState && this.appState.page === 'account') {
      if (e.detail.provider === 'oidc') {
        // For HelseID: bruk vanlig innlogging - currentAccessToken gjør at det blir linking
        await openHelseIdAuth(true);
      } else if (e.detail.provider === 'criipto') {
        // For BankID: bruk samme løsning som HelseID - currentAccessToken gjør at det blir linking
        await openCriiptoAuth(true);
      }
    }
  }

  async onUnlinkAuth(e: CustomEvent<{ provider: 'oidc' | 'criipto' }>) {
    if (this.appState && this.appState.page === 'account') {
      const message = {
        authDescription:
          e.detail.provider === 'oidc'
            ? (this.appState.user.authDescription ?? '')
            : (this.appState.user.criiptoAuthDescription ?? ''),
        provider: e.detail.provider,
      };
      await this.api.accounts.unlinkAuthentication({
        unlinkAuthMessage: message,
      });
      await this.onRequestLogout();
    }
  }

  onTutorialStateChanged(e: CustomEvent<TutorialState>) {
    const state = getStore().getState();
    const participantId = state.currentTutorialParticipantId;

    if (!participantId || !state.id) {
      console.warn('[Tutorial] Cannot update state: missing participantId or organizationId');
      return;
    }

    // Step 1: Optimistic update (immediate UI feedback)
    getStore().dispatch(
      updateCurrentTutorialParticipantState({
        currentPart: e.detail.currentPart,
        partStates: e.detail.partStates,
        rating: e.detail.rating,
      }),
    );

    // Step 2: Debounced API call (reduces server load)
    this.debouncedTutorialStateChanged(e.detail);
  }

  async onDisconnectSecureLogin() {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await this.api.organization.updateSecureLogin({
        updateSecureLoginRequest: {
          requiresSecureLogin: false,
        },
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
      });
      window.location.href = '/';
    }
  }

  async onConnectSecureLogin() {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await this.api.organization.updateSecureLogin({
        updateSecureLoginRequest: {
          requiresSecureLogin: true,
        },
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
      });
      window.location.href = '/';
    }
  }

  async onMapElementChanged(e: CustomEvent<MapElementChangedDetail>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await mapElementChangedHandler(
        this.api,
        e,
        (s: string) => this.slideNotification({ primaryText: s }),
        this.appState.viewModel.currentOrganizationId.toString(),
      );
    }
  }

  async onMapElementDeleted(e: CustomEvent<{ uuid: string; unitType: string }>) {
    await mapElementDeletedHandler(this.api, e);
  }

  async onNewPartner(e: CustomEvent<{ uuid: string; name: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await createPartner(this.api, e.detail.uuid, e.detail.name, '' + this.appState.viewModel.currentOrganizationId);
      this.slideNotification({ primaryText: t('app.partnerAdded', { name: e.detail.name ?? '' }) });
    }
  }

  async onNewNetwork(e: CustomEvent<{ item: InfosecNetwork }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const createNetworkRequest: CreateOrUpdateNetworkRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        networkId: e.detail.item.uuid,
        networkUpdateMessage: {
          name: e.detail.item.name,
          connectionType: e.detail.item.connectionType,
          networkType: e.detail.item.type as NetworkUpdateMessageNetworkTypeEnum,
          isConfirmedEntity: true,
        },
      };
      await this.api.computers.createOrUpdateNetwork(createNetworkRequest);
    }
  }

  async onNewComputer(e: CustomEvent<{ uuid: string; name: string; type: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const createComputerRequest: CreateOrUpdateComputerRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        computerId: e.detail.uuid,
        computerUpdateMessage: {
          location: '',
          name: e.detail.name,
          type: e.detail.type,
          networkUuid: '',
          unitType: 'computer',
          serviceProvider: '',
          connectionType: '',
          serviceProviderContract: '',
          systemUpdateOperatorType: 'UNDEFINED',
          systemUpdateOperator: undefined,
          antiVirusOperatorType: 'UNDEFINED',
          antiVirusOperator: undefined,
          antiVirusRequirements: [],
          locked: false,
          restrictedPhysicalAccess: false,
          displayPositionSecure: false,
          elevated: false,
          mobileMedicalDataRequirements: [],
          notes: undefined,
          isConfirmedEntity: true,
        },
      };
      await this.api.computers.createOrUpdateComputer(createComputerRequest);
    }
  }

  async onNewRiskAssessment(e: CustomEvent<{ uuid: string; name: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await createRiskAssessment(
        this.api,
        e.detail.uuid,
        e.detail.name,
        '' + this.appState.viewModel.currentOrganizationId,
      );
    }
  }

  async onNewEmployee(e: CustomEvent<{ uuid: string; name: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await createEmployee(this.api, e.detail.uuid, e.detail.name, '' + this.appState.viewModel.currentOrganizationId);
    }
  }

  async onNewContract(e: CustomEvent<{ uuid: string; name: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await createContract(this.api, e.detail.uuid, e.detail.name, '' + this.appState.viewModel.currentOrganizationId);
    }
  }

  async onNewAsset(e: CustomEvent<{ uuid: string; name: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await createAsset(this.api, e.detail.uuid, e.detail.name, '', '' + this.appState.viewModel.currentOrganizationId);
    }
  }

  async onSendFeedback(e: CustomEvent<{ url: string; message: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await this.api.accounts.sendFeedback({
        feedbackMessage: {
          email: this.appState.user.username,
          message: e.detail.message,
          url: e.detail.url,
        },
      });
    }
  }

  async onCreateMeetingReport(e: CustomEvent<{ uuid: string; href: string }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const reportUuid = uuid();

      const p2: CreateMeetingOccurrenceReportRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        createMeetingOccurrenceReportCommand: {
          uuid: e.detail.uuid,
          reportUuid: reportUuid,
        },
      };
      await this.api.meetingOccurrences.createMeetingOccurrenceReport(p2);

      const s = this.currentPathArray.join('/');
      const targetUrl = s + '/reports/' + reportUuid + '?edit';
      window.history.pushState({}, '', targetUrl);
      await handleNavigation(window.location);
    }
  }

  async onSendMeetingNotice(e: CustomEvent<MessageForSend>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const p2: SendMeetingOccurrenceNoticeRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        sendMeetingOccurrenceNoticeCommand: {
          uuid: e.detail.occurrenceUuid,
          message: e.detail.message,
          includeAgenda: e.detail.includeContent,
          recipients: [...e.detail.employeeRecipients, ...e.detail.contactRecipients],
        },
      };
      await this.api.meetingOccurrences.sendMeetingOccurrenceNotice(p2);
    }
  }

  async onSendMeetingReport(e: CustomEvent<MessageForSend>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const p2: SendMeetingOccurrenceReportRequest = {
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        sendMeetingOccurrenceReportCommand: {
          uuid: e.detail.occurrenceUuid,
          message: e.detail.message,
          includeReport: e.detail.includeContent,
          recipients: [...e.detail.employeeRecipients, ...e.detail.contactRecipients],
        },
      };
      await this.api.meetingOccurrences.sendMeetingOccurrenceReport(p2);
    }
  }

  async onUpdateUserAccess(e: UpdateUserAccessEvent) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      if (e.detail.userType === 'EMPLOYEE') {
        await this.api.employees.updateEmployeeAccess({
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          entityId: e.detail.userUuid,
          updateAccessCommand: { accessLevel: e.detail.accessLevel, email: e.detail.email },
        });
      } else {
        const o = getStore().getState().organization;
        if (o === undefined) {
          throw new Error('Illegal state (E435)');
        }
        const c = o.contactsById[e.detail.userUuid];
        if (c === undefined) {
          throw new Error('Illegal state (E435)');
        }
        await this.api.partners.postUpdateAccessForContact({
          partnerId: c.partnerUuid,
          entityId: e.detail.userUuid,
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          updateAccessCommand: {
            accessLevel: e.detail.accessLevel,
            email: e.detail.email,
            accessExpires: e.detail.accessExpires,
          },
        });
      }
    }
  }

  async onEmployeeGender(e: CustomEvent<{ uuid: string; gender: EmployeeViewModelGenderEnum }>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await this.api.employees.updateEmployeeGender({
        organizationId: '' + this.appState.viewModel.currentOrganizationId,
        entityId: e.detail.uuid,
        updateGenderCommand: {
          gender: e.detail.gender,
        },
      });
    }
  }

  async onTaskDone(e: CustomEvent<{ taskUuid: string }>) {
    await getStore().dispatch(taskDone(this.api, e.detail.taskUuid));
    getStore().dispatch(updateSelectedStartTask(''));
  }

  async onExternalConnectionChange(e: CustomEvent<NetworkExternalConnectionChange>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await externalConnectChangeHandler(this.api, e, '' + this.appState.viewModel.currentOrganizationId);
    }
  }

  /**
   * For dev purposes
   */
  getRelativeUrl(url: string): string {
    const urlObj = new URL(url);
    return window.location.origin + urlObj.pathname + urlObj.search;
  }

  async onCheckEmail(e: CustomEvent<{ email: string; callback: (_exists: boolean) => void }>) {
    try {
      const r = await checkEmail(e.detail.email);
      e.detail.callback(r.message === 'exists');
    } catch {
      // Always invoke the callback so the signup form clears its loading
      // state instead of hanging, then inform the user. (issue #1531)
      e.detail.callback(false);
      displayAlert(t('app.couldNotCheckEmail'));
    }
  }

  async onCreateUser(e: CustomEvent<{ email: string; module: string; serviceType: string }>) {
    try {
      const r = await createUser({
        createUserCommand: {
          email: e.detail.email,
          module: e.detail.module,
          serviceType: e.detail.serviceType,
        },
      });
      const target = this.getRelativeUrl(r.target);
      setTimeout(() => {
        window.location.href = target;
      }, 400);
    } catch {
      displayAlert(t('app.couldNotCreateUser'));
    }
  }

  async onCreateOrganization(e: CustomEvent<NewOrganization>) {
    try {
      const accounts = this.api.accounts;
      const r = await accounts.createOrganization({
        createOrganizationInput: {
          number: e.detail.number,
          name: e.detail.name,
          ownerEmail: e.detail.ownerEmail,
          addons: e.detail.addons,
          ownerFirstName: e.detail.ownerFirstName,
          ownerLastName: e.detail.ownerLastName,
          address: e.detail.address,
          postcode: e.detail.postcode,
          locality: e.detail.locality,
          module: e.detail.module.toUpperCase(),
          plan: e.detail.plan,
          employeesCount: e.detail.employeesCount,
          specialTerms: e.detail.specialTerms,
          sector: e.detail.sector,
        },
      });
      const a = r.target.split('/');
      const payload = a[5];
      const str = payload.replace(/-/g, '+').replace(/_/g, '/');
      const payloadObject = JSON.parse(atob(str));
      const id = payloadObject.targetUrl.split('/')[2];
      const value = e.detail.plan === 'X' ? 1600 : 7500;
      const target = this.getRelativeUrl(r.target);
      const searchParams = new URLSearchParams();

      searchParams.append('t', target);
      searchParams.append('p', e.detail.plan + '_' + e.detail.module.toUpperCase() + '_' + e.detail.specialTerms);
      searchParams.append('id', id);
      searchParams.append('v', value.toString());

      const baseUrl = new URL('https://trinnvis.no/thankyou.html');

      // Attach search parameters to the base URL
      baseUrl.search = searchParams.toString();

      window.location.href = baseUrl.toString();
    } catch {
      displayAlert(t('app.couldNotCreateOrganization'));
    }
  }

  async onAccessChange(e: CustomEvent<AccessChange>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await accessChangeHandler(this.api, e, '' + this.appState.viewModel.currentOrganizationId);
    }
  }

  async onSaveWorkday(
    e: CustomEvent<{
      value: EditException;
      employeeUuid: string;
      sameAsScheduled: boolean;
      day: string;
    }>,
  ) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      if (e.detail.sameAsScheduled) {
        await this.api.employees.deleteEmployeeWorkScheduleException({
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          entityId: e.detail.employeeUuid,
          date: e.detail.day,
        });
      } else {
        const exception: WorkScheduleException = {
          date: e.detail.day,
          work: e.detail.value.type === 'work',
          start: e.detail.value.start,
          end: e.detail.value.end,
        };
        await this.api.employees.createOrUpdateEmployeeWorkScheduleException({
          organizationId: '' + this.appState.viewModel.currentOrganizationId,
          entityId: e.detail.employeeUuid,
          date: exception.date,
          workScheduleExceptionUpdateMessage: {
            workScheduleException: exception,
          },
        });
      }
    }
  }

  async onBackupChange(e: CustomEvent<BackupChange>) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      await backupChangeHandler(this.api, e);
    }
  }

  onRenameAttachment(
    e: CustomEvent<{
      document: FileViewerDocument;
    }>,
  ) {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      getStore().dispatch(
        renameAttachment(
          this.api,
          e.detail.document.entityType,
          e.detail.document.uuid,
          e.detail.document.attachmentId,
          e.detail.document.name,
        ),
      );
    }
  }

  onDeleteAttachment(
    e: CustomEvent<{
      document: FileViewerDocument;
    }>,
  ) {
    if (this.appState !== undefined && this.appState.page === 'account' && this.appState.viewModel?.loaded === true) {
      getStore().dispatch(
        deleteAttachment(
          this.api,
          e.detail.document.entityType,
          e.detail.document.uuid,
          e.detail.document.attachmentId,
        ),
      );
    }
  }

  async onCancelSigningOrder(
    e: CustomEvent<{
      entityType: string;
      entityUuid: string;
      signingOrderId: string;
    }>,
  ) {
    const o = getStore().getState().organization;
    if (o !== undefined) {
      await this.api.organization.cancelSignatures({
        organizationId: '' + o.organizationId,
        cancelSignaturesCommand: {
          entityType: e.detail.entityType,
          entityUuid: e.detail.entityUuid,
          signatureOrderId: e.detail.signingOrderId,
        },
      });
    }
  }

  async onCreateSignaturePerson(
    e: CustomEvent<{
      uuid: string;
      firstName: string;
      lastName: string;
      email?: string;
      partnerUuid: string;
      personalIdentifier?: string;
    }>,
  ) {
    const o = getStore().getState().organization;
    if (o !== undefined) {
      await this.api.organization.createSignaturePerson({
        organizationId: '' + o.organizationId,
        createSignaturePersonCommand: {
          uuid: e.detail.uuid,
          firstName: e.detail.firstName,
          lastName: e.detail.lastName,
          email: e.detail.email,
          partnerUuid: e.detail.partnerUuid,
          personalIdentifier: e.detail.personalIdentifier,
        },
      });
    }
  }

  async onUpdateSignaturePerson(
    e: CustomEvent<{
      uuid: string;
      email?: string;
      personalIdentifier?: string;
    }>,
  ) {
    const o = getStore().getState().organization;
    if (o !== undefined) {
      await this.api.organization.updateSignaturePerson({
        organizationId: '' + o.organizationId,
        updateSignaturePersonCommand: {
          uuid: e.detail.uuid,
          email: e.detail.email,
          personalIdentifier: e.detail.personalIdentifier,
        },
      });
    }
  }

  async onExtendSigningOrder(
    e: CustomEvent<{
      entityType: string;
      entityUuid: string;
      signingOrderId: string;
      dueDate: string;
    }>,
  ) {
    const o = getStore().getState().organization;
    if (o !== undefined) {
      await this.api.organization.extendSignatures({
        organizationId: '' + o.organizationId,
        extendSignaturesCommand: {
          entityType: e.detail.entityType,
          entityUuid: e.detail.entityUuid,
          signatureOrderId: e.detail.signingOrderId,
          dueDate: e.detail.dueDate,
        },
      });
    }
  }

  async onRequestSignatures(
    e: CustomEvent<{
      entityType: string;
      entityUuid: string;
      signeesForOrganization: string[];
      signeesForPartners: string[];
      signeesAsEmployees: string[];
      content: ContentItem[];
      message: string;
      dueDate: LocalDate;
    }>,
  ) {
    const o = getStore().getState().organization;
    if (o !== undefined) {
      const signatories: RequestSignaturesSignatory[] = [
        ...e.detail.signeesForOrganization.map((x): RequestSignaturesSignatory => {
          return {
            uuid: x,
            role: 'ORGANIZATION',
          };
        }),
        ...e.detail.signeesForPartners.map((x): RequestSignaturesSignatory => {
          return {
            uuid: x,
            role: 'PARTNER',
          };
        }),
        ...e.detail.signeesAsEmployees.map((x): RequestSignaturesSignatory => {
          return {
            uuid: x,
            role: 'EMPLOYEE',
          };
        }),
      ];

      const documents = e.detail.content.map((x) => {
        if (x.type === 'textContent') {
          return e.detail.entityUuid;
        } else {
          return x.value;
        }
      });

      await this.api.organization.requestSignatures({
        organizationId: '' + o.organizationId,
        requestSignaturesCommand: {
          entityType: e.detail.entityType,
          entityUuid: e.detail.entityUuid,
          message: e.detail.message,
          signatories: signatories,
          dueDate: e.detail.dueDate.toString(),
          documents: documents,
        },
      });
    }
  }

  async onDisplayFeedback() {
    const result: FeedbackResult = await DFeedbackDialog.open({ message: '' });
    if (result.action === 'done') {
      await this.onSendFeedback(
        new CustomEvent<{ url: string; message: string }>('send-feedback', {
          bubbles: true,
          composed: true,
          detail: {
            url: location.href,
            message: result.message,
          },
        }),
      );
    }
  }

  async onDisplayNewIssue() {
    if (this.appState && this.appState.page === 'account') {
      const applicationViewModel = this.appState.viewModel;
      if (applicationViewModel !== undefined && applicationViewModel.loaded) {
        await NewIssueDialog.open({
          organizationId: applicationViewModel.currentOrganizationId,
          currentEmployeeUuid: applicationViewModel.currentEmployeeUuid,
          recipientOptions: applicationViewModel.recipientOptionsForIssues,
          canProcessIssue: applicationViewModel.currentEmployee?.accessLevel === 'USER',
        });
      }
    }
  }

  private async processUploadsWithNotification(organizationId: string, _initialCount: number) {
    const startTime = Date.now();
    const THRESHOLD_MS = 5000; // 5 seconds
    const THRESHOLD_FILES = 3; // 3 files remaining

    let notificationShown = false;

    const results = await processUploads(organizationId, (completed, total) => {
      const elapsed = Date.now() - startTime;
      const remaining = total - completed;

      if (!notificationShown && elapsed > THRESHOLD_MS && remaining >= THRESHOLD_FILES) {
        this.slideNotification({
          primaryText: t('app.uploadsContinueInBackground'),
        });
        notificationShown = true;
      }
    });

    // Show notification for already uploaded files
    const alreadyUploadedCount = results.filter((r) => r.duplicate).length;
    if (alreadyUploadedCount > 0) {
      this.slideNotification({
        primaryText:
          alreadyUploadedCount === 1
            ? t('app.safetyDatasheetAlreadyUploadedSingular')
            : t('app.safetyDatasheetAlreadyUploadedPlural', { count: alreadyUploadedCount }),
      });
    }
  }

  /**
   * Reload tutorials data without reloading entire organization
   */
  private async reloadTutorials() {
    const state = getStore().getState();
    if (!state.id) return;

    try {
      const tutorials = await fetchTutorials(this.api, state.id);
      getStore().dispatch(loadedTutorials(tutorials));

      // Preload tutorial content for all tutorials (only if not already cached)
      const currentState = getStore().getState();
      const uniqueVersions = new Map<string, { tutorialId: number; contentVersionId: string }>();

      // Collect all unique contentVersionId across all tutorials
      tutorials.forEach((tutorial) => {
        if (tutorial.contentVersionId) {
          const cacheKey = `${tutorial.id}-${tutorial.contentVersionId}`;
          // Only add if not already in cache
          if (!currentState.tutorialContentCache[cacheKey]) {
            uniqueVersions.set(cacheKey, {
              tutorialId: parseInt(tutorial.id, 10),
              contentVersionId: tutorial.contentVersionId,
            });
          }
        }
      });

      if (uniqueVersions.size > 0) {
        // Load all content versions in parallel
        const preloadPromises = Array.from(uniqueVersions.values()).map(async ({ tutorialId, contentVersionId }) => {
          try {
            const cacheKey = `${tutorialId}-${contentVersionId}`;
            const content = await fetchTutorialContent(this.api, state.id!, tutorialId, contentVersionId);
            getStore().dispatch(cacheTutorialContent(cacheKey, content));
          } catch (error) {
            console.error(`[reloadTutorials] Failed to preload content for tutorial ${tutorialId}:`, error);
            // Don't throw - we don't want to break the flow if one content fails to load
          }
        });

        await Promise.all(preloadPromises);
      }
    } catch (error) {
      console.error('[reloadTutorials] Failed to reload tutorials:', error);
    }
  }

  private async loadTutorialContentIfNeeded(participantId: string) {
    const state = getStore().getState();
    const organization = getOrganization(state);
    if (!organization?.tutorials) return;

    // Find tutorial and registration for this participant
    let tutorialId: string | undefined;
    let contentVersionId: string | undefined;

    for (const tutorial of organization.tutorials) {
      if (!tutorial.registrations) continue;

      for (const registration of tutorial.registrations) {
        if (!registration.participants) continue;

        for (const participant of registration.participants) {
          if (participant.id === participantId) {
            tutorialId = tutorial.id;
            contentVersionId = registration.contentVersionId;
            break;
          }
        }
        if (tutorialId) break;
      }
      if (tutorialId) break;
    }

    if (!tutorialId || !contentVersionId) {
      console.warn('[Tutorial] Could not find tutorial or contentVersionId for participant:', participantId);
      return;
    }

    // Check if content is already cached
    const cacheKey = `${tutorialId}-${contentVersionId}`;
    const contentAlreadyCached = !!state.tutorialContentCache[cacheKey];

    // Fetch content and participant state in parallel
    try {
      const promises: Promise<void>[] = [];

      // Fetch content if not cached
      if (!contentAlreadyCached) {
        promises.push(
          fetchTutorialContent(this.api, state.id!, parseInt(tutorialId, 10), contentVersionId).then((content) => {
            getStore().dispatch(cacheTutorialContent(cacheKey, content));
          }),
        );
      }

      // Always fetch participant state (it's dynamic, not cached)
      promises.push(
        fetchParticipantState(this.api, state.id!, parseInt(tutorialId, 10), participantId).then((participantState) => {
          getStore().dispatch(updateCurrentTutorialParticipantState(participantState));
        }),
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('[Tutorial] Failed to fetch tutorial data:', error);
      // Don't throw - let the viewer show what it can
    }
  }

  /**
   * Performs the actual API call to update tutorial participant state.
   * Includes retry logic and rollback on permanent failure.
   */
  private async performTutorialStateUpdate(detail: TutorialState, retryCount = 0): Promise<void> {
    const state = getStore().getState();
    const participantId = state.currentTutorialParticipantId;

    if (!participantId || !state.id) {
      console.warn('[Tutorial] Cannot update state: missing participantId or organizationId');
      return;
    }

    const maxRetries = 2;
    const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s

    try {
      const updatedState = await updateParticipantState(
        this.api,
        state.id,
        parseInt(detail.tutorialId, 10),
        participantId,
        {
          currentPart: detail.currentPart,
          partStates: detail.partStates,
          rating: detail.rating,
        },
      );

      // Update Redux state with server response (server wins)
      getStore().dispatch(updateCurrentTutorialParticipantState(updatedState));
    } catch (error) {
      console.error('[Tutorial] API call failed:', {
        error,
        attempt: retryCount + 1,
        maxRetries: maxRetries + 1,
      });

      // Retry logic with exponential backoff
      if (retryCount < maxRetries) {
        setTimeout(() => {
          this.performTutorialStateUpdate(detail, retryCount + 1);
        }, retryDelay);
      } else {
        // Permanent failure: rollback to server state
        console.error('[Tutorial] Permanent failure after retries, fetching fresh state from server');
        try {
          const freshState = await fetchParticipantState(
            this.api,
            state.id,
            parseInt(detail.tutorialId, 10),
            participantId,
          );
          getStore().dispatch(updateCurrentTutorialParticipantState(freshState));
          this.slideNotification({
            primaryText: t('app.couldNotSaveProgress'),
          });
        } catch (fetchError) {
          console.error('[Tutorial] Failed to fetch fresh state:', fetchError);
          this.slideNotification({
            primaryText: t('app.couldNotUpdateProgress'),
          });
        }
      }
    }
  }

  private async setupDeepLinkHandler() {
    // Only setup deep link handler on native platforms
    if (!runningUnderNativePlatform()) {
      return;
    }

    if (this.deepLinkListenerHandle) {
      return;
    }

    this.deepLinkListenerHandle = await App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      let targetPath: string;

      try {
        // Parse URL to extract path, query parameters, and hash
        // iOS uses: no.trinnvis.dabih://
        // Android uses: no.trinnvis.dabih.issues://
        const PARSE_BASE_URL = 'https://app.trinnvis.no/';
        let urlToParse: string;

        if (event.url.startsWith('no.trinnvis.dabih://')) {
          // iOS custom scheme: no.trinnvis.dabih://account/123/documents/abc?edit=true#section
          urlToParse = event.url.replace('no.trinnvis.dabih://', PARSE_BASE_URL);
        } else if (event.url.startsWith('no.trinnvis.dabih.issues://')) {
          // Android custom scheme: no.trinnvis.dabih.issues://account/123/documents/abc?edit=true#section
          urlToParse = event.url.replace('no.trinnvis.dabih.issues://', PARSE_BASE_URL);
        } else if (event.url.includes('app.trinnvis.no')) {
          // HTTPS URL: https://app.trinnvis.no/account/123/documents/abc?edit=true#section
          urlToParse = event.url;
        } else {
          console.warn('[Deep Link] Unknown URL scheme:', event.url);
          return;
        }

        // Parse the URL to extract all components
        const parsedUrl = new URL(urlToParse);
        targetPath = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;

        // Ensure path starts with /
        if (!targetPath.startsWith('/')) {
          targetPath = '/' + targetPath;
        }
      } catch (error) {
        console.error('[Deep Link] Failed to parse URL:', event.url, error);
        return;
      }

      if (!targetPath) {
        console.warn('[Deep Link] No valid path extracted from URL:', event.url);
        return;
      }

      // Check if user is logged in
      const state = getStore().getState();
      if (!state.user) {
        // User is not logged in - save the target URL for after login
        sessionStorage.setItem('targetUrl', targetPath);
        return;
      }

      // User is logged in - navigate immediately
      // The organization ID is part of the URL path, so we don't need to wait for it to load
      window.history.pushState({}, '', targetPath);
      await handleNavigation(window.location);
    });
  }

  private handleNetworkRecovery() {
    // Refresh critical data when network comes back online
    const state = getStore().getState();
    const organizationId = state.id;

    if (organizationId && state.user) {
      // Always reload organization data when network returns
      // loadOrganization will use background refresh if data already loaded (via state.loaded check)
      getStore().dispatch(loadOrganization(organizationId, state.user, this.api));

      // Re-establish Ably connection if needed
      if (this.ablyClient && this.ablyClient.connection.state !== 'connected') {
        this.ablyClient.connect();
      }
    }
  }

  private async handleLeaveOrganization(e: CustomEvent<{ organizationId: number }>) {
    const { organizationId } = e.detail;

    try {
      // Build headers - check for admin token first (Basic auth), then OAuth token (Bearer auth)
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      const adminToken = localStorage.getItem('dabih-token');
      if (adminToken) {
        // Admin login - use Basic authentication
        headers.Authorization = `Basic ${adminToken}`;
      } else {
        // OAuth login - use Bearer token
        const accessToken = await getValidAccessToken();
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }
      }

      // Call API to leave organization
      // POST /organizations/{organizationId}/:remove-access
      const response = await fetch(`${BASE_PATH}/organizations/${organizationId}/:remove-access`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          confirmation: true,
        }),
        credentials: 'include', // Include credentials for session-based fallback
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to leave organization: ${errorText}`);
      }

      // Reload user data to update organizations list
      window.location.reload();
    } catch (error) {
      console.error('[DApp] Failed to leave organization:', error);
      alert(t('app.couldNotLeaveOrganization', { error: String(error) }));
    }
  }

  private async handleDeleteOrganization(e: CustomEvent<{ organizationId: number }>) {
    const { organizationId } = e.detail;

    try {
      // Build headers - check for admin token first (Basic auth), then OAuth token (Bearer auth)
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      const adminToken = localStorage.getItem('dabih-token');
      if (adminToken) {
        // Admin login - use Basic authentication
        headers.Authorization = `Basic ${adminToken}`;
      } else {
        // OAuth login - use Bearer token
        const accessToken = await getValidAccessToken();
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }
      }

      // Call API to delete organization
      // POST /organizations/{organizationId}/:close-account
      const response = await fetch(`${BASE_PATH}/organizations/${organizationId}/:close-account`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          confirmation: true,
        }),
        credentials: 'include', // Include credentials for session-based fallback
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete organization: ${errorText}`);
      }

      // Reload user data to update organizations list
      window.location.reload();
    } catch (error) {
      console.error('[DApp] Failed to delete organization:', error);
      alert(t('app.couldNotDeleteOrganization', { error: String(error) }));
    }
  }

  private verifyDraftRemoval(entityType: string, entityUuid: string, organizationId: string) {
    // Retry with exponential backoff: 1s, 2s, 4s (max 3 attempts)
    const maxRetries = 3;
    const baseDelay = 1000;

    const checkDraftStatus = async (attempt: number): Promise<void> => {
      try {
        const state = getStore().getState();
        const items = itemsByTypeAndId(state);

        if (items && items[entityType]) {
          const entity = items[entityType][entityUuid];

          if (entity && entity.hasDraft) {
            if (attempt < maxRetries) {
              // Still has draft, but we have retries left
              const nextDelay = baseDelay * Math.pow(2, attempt);
              setTimeout(() => checkDraftStatus(attempt + 1), nextDelay);
              return;
            }

            // All retries exhausted - report error and fetch fresh data
            const errorMessage = `Draft status not removed for ${entityType} ${entityUuid} after ${maxRetries} attempts`;
            console.error(errorMessage);

            // Report to monitoring only after all retries failed
            notifyError(new Error(errorMessage), {
              metadata: {
                entity: {
                  type: entityType,
                  uuid: entityUuid,
                  organizationId: organizationId,
                  hasDraft: entity.hasDraft,
                  attempts: maxRetries,
                },
              },
            });

            // Fetch fresh data from API as last resort
            try {
              // Build the path for the entity
              let path = `/organizations/${organizationId}`;
              if (entityType === 'tasks' && entity.functionUuid) {
                path += `/functions/${entity.functionUuid}/tasks/${entityUuid}`;
              } else if (entityType === 'contacts' && entity.partnerId) {
                path += `/partners/${entity.partnerId}/contacts/${entityUuid}`;
              } else {
                path += `/${entityType}/${entityUuid}`;
              }

              await getStore().dispatch(
                updateItem(this.api, {
                  path: path,
                  eventType: 'update',
                }),
              );
            } catch (fetchError) {
              console.error('Failed to fetch fresh entity data:', fetchError);
              notifyError(fetchError, {
                metadata: {
                  entity: {
                    type: entityType,
                    uuid: entityUuid,
                    organizationId: organizationId,
                  },
                },
              });
            }
          }
        }
      } catch (error) {
        console.error('Error verifying draft removal:', error);
        notifyError(error);
      }
    };

    // Start verification after initial delay
    setTimeout(() => checkDraftStatus(0), baseDelay);
  }

  private async showMessageWithTimeout(
    operation: () => Promise<void>,
    showMessage: () => void,
    hideMessage: () => void,
  ): Promise<void> {
    const delayBeforeShow = 300; // Delay before showing the message (ms)
    const minMessageDisplayTime = 600; // Minimum time to show the message (ms)

    let messageShownAt: number | null = null;

    // Create a promise that resolves when the message should be shown
    const showMessagePromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        messageShownAt = Date.now(); // Record the time the message is shown
        showMessage();
        resolve();
      }, delayBeforeShow);
    });

    // Start the operation and the message timer simultaneously
    const operationPromise = operation();

    // Wait for whichever finishes first (message timer or operation)
    await Promise.race([showMessagePromise, operationPromise]);

    // Wait for the operation to complete
    await operationPromise;

    if (messageShownAt !== null) {
      // If the message was shown, calculate the remaining display time
      const timeShown = Date.now() - messageShownAt;
      const remainingTime = Math.max(0, minMessageDisplayTime - timeShown);

      // Wait for the remaining time to ensure minimum display duration
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      // Hide the message
      hideMessage();
    }
  }

  private async doSaveDraft(
    e: CustomEvent<{
      entityType: string;
      entityUuid: string;
      draft: Record<string, unknown>;
      done: () => void;
    }>,
  ) {
    if (this.appState && this.appState.page === 'account' && this.appState.viewModel?.loaded) {
      const state = getStore().getState();
      const o = getOrganization(state);
      if (o !== undefined) {
        const requestParameters = {
          organizationId: '' + o.organizationId,
          saveDraftCommand: {
            entityType: e.detail.entityType === 'contacts' ? 'contactPersons' : e.detail.entityType,
            entityUuid: e.detail.entityUuid,
            draft: e.detail.draft,
          },
        };
        if (!isEqual(this.lastDraftSaved, requestParameters)) {
          this.lastDraftSaved = requestParameters;
          await this.api.organization.saveDraft(requestParameters);
        }
      }
    }
  }

  private async updateAppState(state: State) {
    return trace('app.updateAppState', () => this.updateAppStateInner(state), {
      path: location.pathname,
    });
  }

  private async updateAppStateInner(state: State) {
    // Stempel denne invokeringen så vi kan kaste resultatet hvis en nyere
    // invokering ble startet under await. Uten dette kan et eldre kalls resultat
    // overskrive et nyere kalls resultat → konto→laster-flimmer ved race mellom
    // konkurrerende updateAppState-kall.
    const seq = ++this.appStateSeq;

    this.currentPathArray = location.pathname.split('/');
    const page = this.currentPathArray[1] ?? '';

    // Deep-link fra QR-kode på infoskjerm-boks: /screen-setup/<playerCode>.
    // Offentlig, uautentisert side – vi går rett til en frittstående
    // screen-setup-side som åpner nettverkskonfig på koden. Ingen innlogging,
    // ingen kontovelger, ingen kryss-org-skjerm-søk: BLE/captive-portal-
    // provisjonering er en lokal operasjon som krever fysisk nærhet til boksen.
    // Settes før auth-sjekken slik at den aldri sendes til /signin. Se
    // d-screen-setup.
    if (page === 'screen-setup') {
      this.appState = { page: 'screenSetup', deviceCode: (this.currentPathArray[2] ?? '').trim() };
      return;
    }

    const { hasPersistedAuth } = await getPersistedAuthState();
    if (seq !== this.appStateSeq) return;

    // Check if OAuth processing is in progress
    if (isOAuthProcessing()) {
      this.appState = {
        page: 'loading',
      };
      // OAuth code processing is handled in redux-behavior.ts handleNavigation
      return;
    }

    // checkRedirectCodeFlow() is already called in redux-behavior.ts handleNavigation when needed
    // Removed duplicate call to prevent OAuth code reuse errors
    let nextAppState: AppState;
    if (hasPersistedAuth) {
      nextAppState = await this.appStateForPage(page, state);
    } else if (page === 'link') {
      nextAppState = await this.appStateForPage(page, state);
    } else if (page === 'signup') {
      nextAppState = await this.appStateForPage(page, state);
    } else if (page === 'callback') {
      nextAppState = {
        page: 'loading',
      };
    } else {
      if (page !== 'signin') {
        this.dispatchEvent(
          new CustomEvent('navigate', {
            composed: true,
            bubbles: true,
            detail: { href: '/signin' },
          }),
        );
      }
      nextAppState = {
        page: 'signin',
      };
    }
    // Forkast hvis en nyere updateAppStateInner har startet mens vi await-et over.
    if (seq !== this.appStateSeq) return;
    this.appState = nextAppState;
  }

  // Oppdater nettleser-tittelen når appState endrer seg, slik at historikken /
  // tilbakeknappen viser meningsfulle titler i stedet for bare «Trinnvis»
  // (issue #7402). Kjøres fra updated() slik at tittelen også oppdateres når
  // entitetsnavnet lastes asynkront: ny viewModel → ny appState → updated().
  protected updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    if (changedProperties.has('appState')) {
      const title = documentTitleForAppState(this.appState);
      if (document.title !== title) {
        document.title = title;
      }
    }
  }

  private async appStateForPage(page: string, state: State): Promise<AppState> {
    switch (page) {
      case 'link':
        return this.appStateForLink();
      case 'account':
        return this.appStateForAccount(state);
      case 'dashboard':
        return this.appStateForDashboard(state);
      case 'me':
        return this.appStateForMe(state);
      case 'p':
        return this.appStateForPageRedirect();
      case 'a':
        return this.appStateForEntityRedirect();
      case '':
        return this.appStateForIndex(state);
      case 'index.html':
        return this.appStateForIndexHtml();
      case 'callback':
        return this.appStateForCallbackRedirect();
      case 'signin':
        return this.appStateForSignin();
      case 'signup':
        return this.appStateForSignup(state);
      default:
        return this.appStatePageNotFound(page);
    }
  }

  private async startListeningToChannels(ablyClient: Realtime, organizationId: number, suffix: string, user: User) {
    const hex = '214c9a797bd5f4ae5a82ec14e28445863829fe8636fab137e1c9c4b779f30257';
    const regExpMatchArray = hex.match(/[\da-f]{2}/gi);
    if (regExpMatchArray === null) {
      throw new Error('Illegal state (E27), cipher params wrong format');
    }
    const numbers = regExpMatchArray.map((h) => parseInt(h, 16));

    const typedArrayKey = new Uint8Array(numbers);

    const cipherParams = {
      key: typedArrayKey,
    };

    const channelOpts = { cipher: cipherParams };

    const channelName = 'organization' + suffix + '-' + organizationId;
    const channel = ablyClient.channels.get(channelName, channelOpts);

    // Handle channel state changes
    channel.on('attached', (stateChange) => {
      if (!stateChange.resumed) {
        // location.reload();
      }
    });

    channel.on('detached', (stateChange) => {
      console.warn(`[Ably] Channel ${channel.name} detached`, stateChange.reason);
      // Don't report to monitoring - detach is expected during reconnection
    });

    channel.on('failed', (stateChange) => {
      console.error(`[Ably] Channel ${channel.name} failed`, stateChange.reason);

      // Check if it's a capability error (40160 - access denied)
      if (stateChange.reason?.code === 40160) {
        console.warn(`[Ably] Channel access denied for ${channel.name} - token may lack permissions`);
        // Don't report capability errors to monitoring - these are configuration/permission issues
      } else {
        // Other channel failures might be bugs, but still don't spam monitoring
        console.error(`[Ably] Unexpected channel failure:`, stateChange.reason);
      }
    });

    channel.on('suspended', (stateChange) => {
      console.warn(`[Ably] Channel ${channel.name} suspended`, stateChange.reason);
      // Don't report to monitoring - suspensions are temporary
    });

    // Per-org batch handler — created once per org, not torn down on switch.
    if (!this.batchNotificationHandlers.has(organizationId)) {
      this.batchNotificationHandlers.set(
        organizationId,
        new AblyBatchNotificationHandler(getStore().dispatch, this.api, organizationId),
      );
    }

    try {
      await channel.subscribe((message) => {
        const m = JSON.parse(message.data);

        // Signage-hendelser (dabih-api #4546): serveren publiserer
        // { eventType: 'signage.*', organizationId, screenId? } UTEN path-felt.
        // Vi refresher skjermdata når noen — typisk en ANNEN bruker — endrer
        // skjermer eller bibliotek i org-en vi ser på. Håndteres før all
        // path-basert ruting under (som ville kastet på manglende m.path) og før
        // SW-videresendingen, som forventer path-baserte entitetsevents.
        if (typeof m.eventType === 'string' && m.eventType.startsWith('signage.')) {
          if (getStore().getState().id === organizationId) {
            this.debouncedReloadScreens();
          }
          return;
        }

        // Always forward to SW with the channel's orgId. SW updates IDB for any
        // org we're subscribed to, regardless of which one the user is viewing.
        void getStateClient().postMessage({
          type: 'ABLY_ENTITY_UPDATED',
          payload: { organizationId, message: m },
        });

        // Redux still owns the active org's UI. Only run Redux-side work for the
        // org the user is currently looking at; non-active orgs are SW-only.
        const activeOrgId = getStore().getState().id;
        const isActiveOrg = activeOrgId === organizationId;

        // Tutorial handling tied to per-tab UI state (currentTutorialParticipantId)
        // — only meaningful for the active org.
        if (m.path.includes('/tutorials/')) {
          if (!isActiveOrg) {
            return;
          }
          const pathParts = m.path.split('/');
          const tutorialId = pathParts[4];
          const entityType = pathParts[5];
          const entityId = pathParts[6];

          this.debouncedReloadTutorials();

          if (entityType === 'participants') {
            const state = getStore().getState();
            if (state.currentTutorialParticipantId === entityId) {
              this.debouncedRefetchCurrentParticipantState(parseInt(tutorialId, 10), entityId);
            }
          }
          return;
        }

        if (!isActiveOrg) {
          return;
        }

        if (m.path.startsWith('/organizations/' + organizationId + '/') && !m.path.endsWith('/archive')) {
          const handler = this.batchNotificationHandlers.get(organizationId);
          if (handler) {
            handler.messageReceived(m);
          } else {
            getStore().dispatch(updateItem(this.api, m));
          }
        }
        if (m.path === '/organizations/' + organizationId) {
          fetchPageGroups(this.api, organizationId).then((pages) => {
            // TEMPORARY OVERRIDE: inject pages the server-generated mal is missing for certain
            // account types (PERFORMANCE_REVIEW, SIGNAGE). Shares the same helper as the
            // loadOrganization/refresh paths so all three stay consistent.
            // TODO: Remove once the server properly includes these pages.
            const processedPages = applyTemporaryPageGroupOverrides(
              pages,
              getStore().getState().organization,
              organizationId,
            );
            getStore().dispatch(loadedPageGroups(processedPages));
          });
          // Don't reload if already loading - loadOrganization handles background refresh when loaded
          const currentState = getStore().getState();
          if (!currentState.loading) {
            getStore().dispatch(loadOrganization(organizationId, user, this.api));
          }
        }
      });
    } catch (error: any) {
      console.error(`[Ably] Failed to subscribe to channel ${channelName}:`, error);

      // Check if it's a capability error
      if (error.code === 40160) {
        console.warn(`[Ably] Channel access denied - user may not have permissions for ${channelName}`);
        // Don't report capability errors to monitoring
        return;
      }

      // Don't report other subscription errors to monitoring - likely network or configuration issues
      console.error(`[Ably] Channel subscription error:`, error);
    }
  }

  private createAblyClient(lastToken: TokenRequest | undefined, user: User) {
    const ablyClient = new Ably.Realtime({
      authCallback: (_params, callback) => {
        if (lastToken) {
          callback('', lastToken);
          lastToken = undefined;
        } else {
          this.api.accounts
            .listOrganizations({
              accountEmail: user.username,
            })
            .then((r) => {
              if (r.ablyTokenRequest !== undefined) {
                callback('', JSON.parse(r.ablyTokenRequest));
              } else {
                console.warn('[Ably] No token request in response');
                // Must call callback with error so Ably knows auth failed
                callback('No token in response', null);
              }
            })
            .catch((error) => {
              console.error('[Ably] Auth callback failed:', error);
              // Don't report auth failures to monitoring - these are expected when network is down
              // Must call callback with error so Ably can retry or fail gracefully
              callback(error?.message || 'Authentication failed', null);
            });
        }
      },
    });

    ablyClient.connection.on('connected', () => {
      this.online = true;
      this.ablyReconnectAttempts = 0; // Reset reconnect counter on successful connection
      if (this.ablyReconnectTimer) {
        clearTimeout(this.ablyReconnectTimer);
        this.ablyReconnectTimer = undefined;
      }
    });

    ablyClient.connection.on('failed', (stateChange) => {
      console.error('[Ably] ✗ Connection failed', stateChange.reason);
      this.online = false;
      // Don't report to monitoring - connection failures are user network issues
      // Failed state is terminal, no automatic reconnect
    });

    ablyClient.connection.on('disconnected', (stateChange) => {
      console.warn('[Ably] ⚠ Disconnected', stateChange.reason);
      this.online = false;
      // Don't report to monitoring - disconnections are expected (network issues, etc.)

      // Implement exponential backoff reconnect
      this.attemptAblyReconnect(ablyClient);
    });

    ablyClient.connection.on('suspended', (stateChange) => {
      console.warn('[Ably] ⚠ Connection suspended', stateChange.reason);
      this.online = false;
      // Don't report to monitoring - suspensions are temporary network issues

      // Attempt reconnect with backoff
      this.attemptAblyReconnect(ablyClient);
    });

    ablyClient.connection.on('closing', () => {});

    ablyClient.connection.on('closed', () => {
      this.online = false;
    });

    return ablyClient;
  }

  private attemptAblyReconnect(ablyClient: Realtime) {
    const maxAttempts = 10;
    const baseDelay = 1000; // 1 second
    const maxDelay = 60000; // 60 seconds

    if (this.ablyReconnectAttempts >= maxAttempts) {
      console.error(`[Ably] Max reconnect attempts (${maxAttempts}) reached, giving up`);
      return;
    }

    this.ablyReconnectAttempts++;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, 60s, 60s, 60s, 60s
    const delay = Math.min(baseDelay * Math.pow(2, this.ablyReconnectAttempts - 1), maxDelay);

    // Clear any existing reconnect timer
    if (this.ablyReconnectTimer) {
      clearTimeout(this.ablyReconnectTimer);
    }

    this.ablyReconnectTimer = window.setTimeout(() => {
      try {
        // Check if already connecting or connected
        const state = ablyClient.connection.state;
        if (state === 'connected' || state === 'connecting') {
          return;
        }

        // Trigger reconnection
        ablyClient.connect();
      } catch (error) {
        console.error('[Ably] Reconnect attempt failed:', error);
        // Don't report to monitoring - reconnect failures are expected
      }
    }, delay);
  }

  private checkForPageRedirect() {
    const t = sessionStorage.getItem('targetPageId');
    if (t !== null) {
      const n = t;
      sessionStorage.removeItem('targetPageId');
      this.onOpenPage(
        new CustomEvent('open-page', {
          composed: true,
          bubbles: true,
          detail: { pageId: Number(n), queryString: '' },
        }),
      );
    } else if (sessionStorage.getItem('targetUrl') !== null) {
      const targetUrl = sessionStorage.getItem('targetUrl');
      sessionStorage.removeItem('targetUrl');
      this.dispatchEvent(new CustomEvent('navigate', { composed: true, bubbles: true, detail: { href: targetUrl } }));
    }
  }

  private async appStateForSignin(): Promise<AppStateSignin | AppStateLoading> {
    const { hasPersistedAuth } = await getPersistedAuthState();

    if (hasPersistedAuth) {
      this.dispatchEvent(
        new CustomEvent('navigate', {
          composed: true,
          bubbles: true,
          detail: { href: '/account' },
        }),
      );
      return { page: 'loading' };
    }
    return { page: 'signin' };
  }

  private async appStateForSignup(state: State): Promise<AppStateSignup> {
    const user: User | undefined = state.user;
    let userForSignup: UserForSignup | undefined = undefined;
    if (user) {
      const featuresMap = {
        MEETINGS: 'meetings',
        MINI: 'complete',
        BASIC: 'complete',
        EXTRA: 'complete',
      };
      userForSignup = {
        email: user.username,
        firstName: user['firstName'] === undefined ? '' : user['firstName'],
        lastName: user['lastName'] === undefined ? '' : user['lastName'],
        organizations: user.organizations.map((o) => {
          return {
            id: o.id,
            name: o.name,
            modules: o.features.map((e) => featuresMap[e] ?? ''),
          };
        }),
      };
    }

    return { page: 'signup', currentUser: userForSignup };
  }

  private async appStateForMe(state: State): Promise<AppStateMe | AppStateNoUser> {
    const user: User | undefined = state.user;
    if (user === undefined) {
      return {
        page: 'noUser',
      };
    } else {
      return { page: 'me', user };
    }
  }

  private appStateForLink(): AppStateLink {
    return { page: 'link', locationSearch: window.location.search };
  }

  /** Rute `/account/{organizationId}/issue-messages/{issueId}` → melderens meldingsside. */
  private appStateForIssueMessages(): AppStateIssueMessages {
    return {
      page: 'issueMessages',
      organizationId: this.currentPathArray[2] ?? '',
      issueId: this.currentPathArray[4] ?? '',
    };
  }

  private async appStateForAccount(
    state: State,
  ): Promise<
    AppStateNoUser | AppStateAccount | AppStateLoading | AppStateNotFound | AppStateSecured | AppStateIssueMessages
  > {
    if (this.currentPathArray[3] === 'issue-messages') {
      return this.appStateForIssueMessages();
    }
    const user = state.user;
    if (user === undefined) {
      return {
        page: 'noUser',
      };
    } else {
      return await this.appStateForUser(user, state);
    }
  }

  private async appStateForDashboard(state: State): Promise<AppStateNoUser | AppStateDashboard> {
    const user = state.user;
    if (user === undefined) {
      return {
        page: 'noUser',
      };
    } else {
      // Create organization objects with data promises
      const organizationsWithData = user.organizations.map((org) => {
        // Contact persons cannot access organization endpoints, skip fetching data for them
        if (org.userType === 'CONTACT') {
          const emptyPromise = Promise.resolve([]);
          const emptyStatsPromise = Promise.resolve({
            totalEmployees: 0,
            employeesWithoutInterview: 0,
            scheduledTotal: 0,
            scheduledWithNotice: 0,
            scheduledWithoutNotice: 0,
            pastTotal: 0,
            pastDone: 0,
            pastWithReport: 0,
            pastNotDone: 0,
          });
          return {
            ...org,
            employeesPromise: emptyPromise,
            meetingOccurrencesPromise: emptyPromise,
            meetingsPromise: emptyPromise,
            meetingStatsPromise: emptyStatsPromise,
          };
        }

        const employeesPromise = this.api.employees
          .listEmployees({ organizationId: org.id.toString() })
          .catch((error) => {
            console.error(`Failed to fetch employees for org ${org.id}:`, error);
            return [];
          });

        const meetingOccurrencesPromise = this.api.meetingOccurrences
          .listMeetingOccurrences({
            organizationId: org.id.toString(),
          })
          .catch((error) => {
            console.warn(`Failed to fetch meeting occurrences for org ${org.id}:`, error);
            return [];
          });

        const meetingsPromise = this.api.meetings
          .listMeetings({
            organizationId: org.id.toString(),
          })
          .catch((error) => {
            console.warn(`Failed to fetch meetings for org ${org.id}:`, error);
            return [];
          });

        // Calculate meeting statistics for employee interviews within ±6 months
        const meetingStatsPromise = Promise.all([employeesPromise, meetingsPromise, meetingOccurrencesPromise])
          .then(([employees, meetings, occurrences]) => {
            // Find the employee interview template ID (template ID 9850 based on templates.ts)
            const employeeInterviewTemplateId = 9850;
            const employeeInterviewMeeting = meetings.find((m) => m.templateId === employeeInterviewTemplateId);

            // Count employees with READONLY or NONE access level (not TERMINATED)
            const relevantEmployees = employees.filter(
              (e) => e.status !== 'TERMINATED' && (e.accessLevel === 'READONLY' || e.accessLevel === 'NONE'),
            );
            const totalEmployees = relevantEmployees.length;

            if (!employeeInterviewMeeting || totalEmployees === 0) {
              return {
                totalEmployees,
                employeesWithoutInterview: totalEmployees,
                scheduledTotal: 0,
                scheduledWithNotice: 0,
                scheduledWithoutNotice: 0,
                pastTotal: 0,
                pastDone: 0,
                pastWithReport: 0,
                pastNotDone: 0,
              };
            }

            // Filter for employee interview occurrences within ±6 months
            const now = new Date();
            const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
            const sixMonthsFromNow = new Date(now.getFullYear(), now.getMonth() + 6, now.getDate());

            const employeeInterviews = occurrences.filter((occ) => {
              if (occ.meetingUuid !== employeeInterviewMeeting.uuid) return false;

              const occDate = new Date(occ.date);
              return occDate >= sixMonthsAgo && occDate <= sixMonthsFromNow;
            });

            // Track which employees have interviews
            const employeesWithInterviewSet = new Set<string>();

            const stats = {
              totalEmployees,
              employeesWithoutInterview: 0,
              scheduledTotal: 0,
              scheduledWithNotice: 0,
              scheduledWithoutNotice: 0,
              pastTotal: 0,
              pastDone: 0,
              pastWithReport: 0,
              pastNotDone: 0,
            };

            employeeInterviews.forEach((occ) => {
              // Track employees (participants) - only count those in our relevant employees list
              occ.participants.forEach((uuid) => {
                if (relevantEmployees.some((e) => e.uuid === uuid)) {
                  employeesWithInterviewSet.add(uuid);
                }
              });

              const occDate = LocalDate.fromString(occ.date);
              const today = LocalDate.now();

              // Meeting is considered "past" only if:
              // - The date is before today, OR
              // - The date is today AND it has been completed (report sent or marked done)
              const isPast = occDate.isBefore(today) || (occDate.isSame(today) && (occ.reportLastSent || occ.doneDate));

              if (isPast) {
                stats.pastTotal++;
                if (occ.doneDate) {
                  stats.pastDone++;
                } else {
                  stats.pastNotDone++;
                }
                if (occ.reportLastSent) {
                  stats.pastWithReport++;
                }
              } else {
                stats.scheduledTotal++;
                if (occ.noticeLastSent) {
                  stats.scheduledWithNotice++;
                } else {
                  stats.scheduledWithoutNotice++;
                }
              }
            });

            stats.employeesWithoutInterview = totalEmployees - employeesWithInterviewSet.size;

            return stats;
          })
          .catch((error) => {
            console.error(`Failed to calculate meeting stats for org ${org.id}:`, error);
            return {
              totalEmployees: 0,
              employeesWithoutInterview: 0,
              scheduledTotal: 0,
              scheduledWithNotice: 0,
              scheduledWithoutNotice: 0,
              pastTotal: 0,
              pastDone: 0,
              pastWithReport: 0,
              pastNotDone: 0,
            };
          });

        return {
          ...org,
          employeesPromise,
          meetingOccurrencesPromise,
          meetingsPromise,
          meetingStatsPromise,
        };
      });

      return {
        page: 'dashboard',
        user: user,
        organizationsWithData,
        helpViewerOpen: false,
        helpViewerPage: '',
      };
    }
  }

  private async appStateForUser(
    user: User,
    state: State,
  ): Promise<AppStateAccount | AppStateLoading | AppStateNotFound | AppStateSecured> {
    const segments = this.currentPathArray;
    if (segments.length > 2 && segments[2] !== '') {
      const id = Number(segments[2]);
      const userOrganization = user.organizations.find((o) => o.id === id);

      // If organization ID is 0 or invalid (user doesn't have access), redirect to account page
      if (!userOrganization || id === 0 || isNaN(id)) {
        window.location.href = '/account';
        return { page: 'loading' };
      }

      return await this.appStateForUserWithOrganization(userOrganization, user, state, segments, id);
    } else {
      return {
        page: 'account',
        user: user,
        helpViewerOpen: state.helpViewerOpen,
        helpViewerPage: state.currentHelpPage,
        writeAccess: false,
      };
    }
  }

  private async appStateForUserWithOrganization(
    userOrganization: OrganizationReference | undefined,
    user: User,
    state: State,
    segments: string[],
    id: number,
  ): Promise<AppStateAccount | AppStateLoading | AppStateNotFound | AppStateSecured> {
    if (userOrganization && userOrganization.requiresSecureLogin && user.provider === '') {
      return { page: 'secured', user: user, selectedUserOrganization: userOrganization };
    } else if (state.loading) {
      return { page: 'loading' };
    } else if (segments[3] === '2300') {
      setTimeout(async () => {
        window.history.pushState({}, '', '/account/' + segments[2] + '/695944');
        await handleNavigation(window.location);
      }, 200);
      return { page: 'loading' };
    } else {
      return await this.appStateForOrganizationAccess(state, segments, userOrganization, user, id);
    }
  }

  private async appStateForOrganizationAccess(
    state: State,
    segments: string[],
    userOrganization: OrganizationReference | undefined,
    user: User,
    id: number,
  ): Promise<AppStateAccount | AppStateLoading | AppStateNotFound> {
    if (state.error) {
      return {
        page: 'notFound',
        requestedPage: segments[2],
      };
    } else {
      if (userOrganization !== undefined && userOrganization.pending) {
        return {
          page: 'account',
          user: user,
          helpViewerOpen: state.helpViewerOpen,
          helpViewerPage: state.currentHelpPage,
          writeAccess: writeAccess(state),
        };
      }

      const o = getStore().getState().organization;
      const currentState = getStore().getState();
      if (o === undefined || o.organizationId !== +id) {
        // Don't reload if already loading - prevents race condition and loading state flash
        if (!currentState.loading) {
          // Sjekk at riktig organisasjon er lastet inn
          getStore().dispatch(loadOrganization(id, user, this.api));
        }
        await this.subscribeToAbly(user, id);
        return {
          page: 'loading',
        };
      }
      this.employeeNamesById = employeeNamesById(state);
      this.partnerNamesById = partnerNamesById(state);

      if (!loaded(state)) {
        return {
          page: 'loading',
        };
      }

      this.checkForPageRedirect();

      // Resume any pending uploads for this organization (once per organization per session)
      const organizationId = id.toString();
      if (this.uploadResumeAttemptedForOrganization !== organizationId) {
        this.uploadResumeAttemptedForOrganization = organizationId;
        // Run in background, don't block UI
        resumePendingUploads(organizationId).catch((error) => {
          console.error('[UploadQueue] Failed to resume pending uploads:', error);
        });
      }

      return {
        page: 'account',
        user: user,
        helpViewerOpen: state.helpViewerOpen,
        helpViewerPage: state.currentHelpPage,
        viewModel: await applicationViewModel(this.api, state),
        writeAccess: writeAccess(state),
      };
    }
  }

  private appStatePageNotFound(page: string): AppStateNotFound {
    return { page: 'notFound', requestedPage: page };
  }

  private appStateForPageRedirect(): AppStateLoading {
    const segments = this.currentPathArray;

    sessionStorage.setItem('targetPageId', segments[2]);

    const o = localStorage.getItem('lastOrganizationId');
    const target1 = o === null ? '' : '/' + o;

    this.dispatchEvent(
      new CustomEvent('navigate', { composed: true, bubbles: true, detail: { href: '/account' + target1 } }),
    );

    return { page: 'loading' };
  }

  private appStateForIndexHtml(): AppStateLoading {
    this.dispatchEvent(new CustomEvent('navigate', { composed: true, bubbles: true, detail: { href: '/' } }));

    return { page: 'loading' };
  }

  private appStateForCallbackRedirect(): AppStateLoading {
    // Preserve query parameters (e.g., ?code=...&state=...&iss=...)
    const queryString = window.location.search;
    const targetUrl = '/' + queryString;

    this.dispatchEvent(new CustomEvent('navigate', { composed: true, bubbles: true, detail: { href: targetUrl } }));

    return { page: 'loading' };
  }

  private appStateForEntityRedirect(): AppStateLoading {
    const segments = this.currentPathArray;
    const o = segments[2];
    const target1 = o === null ? '' : '/' + o;

    this.dispatchEvent(
      new CustomEvent('navigate', {
        composed: true,
        bubbles: true,
        detail: { href: '/account' + target1 + '/20?tab=map&c=' + segments[3] },
      }),
    );

    return { page: 'loading' };
  }

  private async appStateForIndex(_state: State): Promise<AppStateLoading> {
    const { hasPersistedAuth } = await getPersistedAuthState();

    if (hasPersistedAuth) {
      const o = localStorage.getItem('lastOrganizationId');
      const target1 = o === null ? '' : '/' + o + '/695944';

      this.dispatchEvent(
        new CustomEvent('navigate', {
          composed: true,
          bubbles: true,
          detail: { href: '/account' + target1 },
        }),
      );
    } else {
      this.dispatchEvent(new CustomEvent('navigate', { composed: true, bubbles: true, detail: { href: '/signin' } }));
    }
    return { page: 'loading' };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-app': DApp;
  }
}
